/**
 * LeadSegments.jsx
 * ─────────────────
 * Lead Segments view — nested inside the Leads page (Pipeline / Segments tabs).
 * Two-column Apple-fluid layout:
 *   • Left 220px: Segment library (Quick starts · My segments · Team segments)
 *   • Right flex: Active segment workspace — header, stats, filter pills,
 *     lead list, bulk action bar.
 *
 * Includes a slide-in Segment Builder (create/edit), Mass Email / SMS /
 * Push-to-campaign modals, and bidirectional sync stubs for the Sales
 * Dialer Step-2 audience filters.
 *
 * Pure-frontend for now — uses real /api/leads when available, falls back
 * to the demo lead dataset so the segments view is always populated in
 * demo mode.
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Filter as FilterIcon, X, Mail, MessageSquare, Play, Download,
  Edit3, Sparkles, ChevronDown, ChevronLeft, Trash2, Send, Calendar as CalendarIcon, Users,
  Loader2, Check, Phone, ExternalLink, Zap,
} from "lucide-react";
import { canExport } from "@/lib/permissions";
import useConfirm from "@/hooks/useConfirm";
import {
  scoreProducts, competitiveAnalysis, difficultyClass, recommendCampaigns,
  generateAISegments, aggregateSegment, vendorBucket, isAPCMEligible, CMS_2026_RATES,
} from "@/lib/aiTargeting";
import CampaignQuickLaunchLightbox from "@/components/CampaignQuickLaunchLightbox";
import { getLeadScore } from "@/lib/getLeadScore";
import { EXTENDED_FILTER_GROUPS, EXTENDED_FILTER_FIELD_MAP, evalExtendedCondition } from "@/lib/leadFieldOptions";

/* ── Practice-type + size derivations (used by filters and AI panel) ── */
const PRODUCT_NAMES = {
  EHR: "MDnet EHR", PM: "Practice Mgmt (RevQ)", RCM: "RevQ AI Billing",
  APCM: "Advanced Primary Care Mgmt", CCM: "Chronic Care Mgmt",
  RPM: "Remote Patient Monitoring", AWV: "Annual Wellness Visit",
};
function deriveSizeBand(lead) {
  const g = lead.practice?.group_size ?? lead.group_size ?? 0;
  if (!g || g <= 1) return "Solo provider";
  if (g <= 5) return "Small group (2–5)";
  if (g <= 25) return "Multi-provider (6–25)";
  if (g <= 100) return "Large group (26–100)";
  return "ACO / IDN (100+)";
}
function derivePracticeType(lead) {
  const sp = (lead.nppes?.specialty_group || lead.specialty || "").toLowerCase();
  if (/cardio|pulmon|nephrol|endocrin|neurolog|gastro|onco|rheumat|dermat|orthop|urolog/.test(sp)) return "Specialty practice";
  if (/family|internal|geriatr|pediatric|primary/.test(sp)) return "Primary care";
  if (/urgent|walk|after.hours/.test(sp)) return "Urgent care";
  if (/aco|accountable.care|integrated|health.system|hospital/.test(sp)) return "ACO / IDN";
  if (/behavioral|psych|mental|addiction/.test(sp)) return "Behavioral health";
  if (/multi.specialty|multispecialty/.test(sp)) return "Multi-specialty";
  return "Specialty practice";
}

/* ── Quick-start system segments (read-only) ──────────────────── */
const QUICK_STARTS = [
  { id: "qs-hot", name: "Hot prospects", dot: "#E24B4A", filter: (l) => (l.rating === "Hot" || (getLeadScore(l)) >= 71) },
  { id: "qs-warm-stale", name: "Warm, 30+ days no contact", dot: "#BA7517", filter: (l) => ((l.rating === "Warm" || ((getLeadScore(l)) >= 41 && (getLeadScore(l)) < 71)) && (l.days_stale || 30) >= 30) },
  { id: "qs-ai-70", name: "AI score 70+", dot: "#378ADD", filter: (l) => (getLeadScore(l)) >= 70 },
  { id: "qs-lost", name: "Closed lost — last 30d", dot: "#888888", filter: (l) => /lost/i.test(l.stage || "") && (l.days_stale || 0) <= 30 },
  { id: "qs-demo-no-prop", name: "Demo completed, no proposal", dot: "#1D9E75", filter: (l) => /demo_completed|demo/i.test(l.stage || "") && !/proposal|quote/i.test(l.stage || "") },
  { id: "qs-decision", name: "Decision pending", dot: "#534AB7", filter: (l) => /decision|negotiation/i.test(l.stage || "") },
  { id: "qs-solo", name: "Solo providers", dot: "#0F6E56", filter: (l) => deriveSizeBand(l) === "Solo provider" },
  { id: "qs-multi", name: "Multi-provider groups (6–25)", dot: "#185FA5", filter: (l) => deriveSizeBand(l) === "Multi-provider (6–25)" },
  { id: "qs-aco", name: "Large groups & ACOs (100+)", dot: "#3C3489", filter: (l) => /Large group|ACO/.test(deriveSizeBand(l)) },
  { id: "qs-primary", name: "Primary-care practices", dot: "#854F0B", filter: (l) => derivePracticeType(l) === "Primary care" },
  { id: "qs-specialty", name: "Specialty practices", dot: "#A32D2D", filter: (l) => derivePracticeType(l) === "Specialty practice" },
];

/* ── Filter field catalogue (shared with Leads + Campaign Step 2) ── */
const FILTER_FIELDS = [
  { id: "practice_type", label: "Practice type", type: "multi", tone: "teal",  options: ["Primary care","Specialty practice","Multi-specialty","Urgent care","ACO / IDN","Behavioral health"] },
  { id: "size_band",     label: "Practice size", type: "multi", tone: "purple",options: ["Solo provider","Small group (2–5)","Multi-provider (6–25)","Large group (26–100)","ACO / IDN (100+)"] },
  { id: "stage",     label: "Stage",            type: "multi", tone: "blue",    options: ["new_lead","contacted","demo","proposal","decision","closed","implementation","inactive"] },
  { id: "specialty", label: "Specialty",        type: "multi", tone: "teal",    options: ["Family Medicine","Internal Medicine","Geriatric Medicine","Cardiology","Pulmonology","Endocrinology","Nephrology","Neurology","Pediatrics"] },
  { id: "nppes_taxonomy", label: "NPPES taxonomy", type: "tag",  tone: "teal",  placeholder: "e.g. 207RC0000X · type or paste codes" },
  { id: "region",    label: "Region",           type: "multi", tone: "amber",   options: ["NE","SE","MW","SW","W","Mountain"] },
  // Iter 188.4 (Feb 2026) — expanded from 12 → 51 codes (50 US states + DC).
  // The Lumina AI parser routinely emits any of the 50 codes (e.g. AL,
  // AR, KS, LA, MN, MS, MO, NE, ND, OK, SD, WI — the 14-state
  // internal-medicine repro prompt); anything not in `options` was
  // silently dropped by mapLuminaConditions -> silent count discrepancy.
  { id: "state",     label: "State",            type: "multi", tone: "amber",   options: ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"] },
  // Iter 188.4 — new field so Lumina's `source` / segment-parse's
  // `lead_source` conditions are representable in the UI instead of
  // being silently dropped by the `recognised` filter.
  { id: "lead_source", label: "Lead source",    type: "multi", tone: "purple",  options: ["Ampliz","EHI Website","Gartner","360 Connect","Software Finder","sales_agent","Quantify","Wix","Google","YellowCake","Referral","conference","facebook","csv_import"] },
  // Iter 140.8 (Peter 49b1eece) — City parity with Leads filter panel.
  { id: "city",      label: "City",             type: "tag",   tone: "amber",   placeholder: "Type a city · multiple allowed" },
  { id: "ai_score",  label: "AI score",         type: "range", tone: "green" },
  { id: "rating",    label: "Lead rating",      type: "multi", tone: "green",   options: ["Hot","Warm","Cold"] },
  { id: "last_contact_days", label: "Last contact (days ago)", type: "range", tone: "purple" },
  { id: "ehr_vendor", label: "EHR vendor",      type: "multi", tone: "teal",    options: ["Epic","Cerner","Practice Fusion","eClinicalWorks","athenahealth","NextGen","Allscripts","(none)"] },
  { id: "medicare_benes", label: "Medicare benes", type: "range", tone: "green" },
  { id: "deal_value", label: "Deal value",      type: "range", tone: "green" },
  { id: "product_fit", label: "Product fit (top recommendation)", type: "multi", tone: "teal", options: ["EHR","PM","RCM","APCM","CCM","RPM","AWV"] },
];

const TONE_PILL = {
  blue:   { bg: "#E6F1FB", color: "#185FA5" },
  amber:  { bg: "#FAEEDA", color: "#854F0B" },
  green:  { bg: "#EAF3DE", color: "#3B6D11" },
  purple: { bg: "#EEEDFE", color: "#3C3489" },
  teal:   { bg: "#E1F5EE", color: "#0F6E56" },
};

/* ── Utility ─────────────────────────────────────────────────── */
function tempLabel(s) { return s >= 71 ? "Hot" : s >= 41 ? "Warm" : "Cold"; }
function ratingPill(r) {
  if (r === "Hot")  return { bg: "#FCEBEB", c: "#A32D2D" };
  if (r === "Warm") return { bg: "#FAEEDA", c: "#854F0B" };
  return { bg: "#E6F1FB", c: "#185FA5" };
}
function initials(name) {
  if (!name) return "??";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
}
function relTime(iso) {
  if (!iso) return "Just now";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
  return `${Math.round(diff / 86400)} d ago`;
}

/* ── localStorage helpers for saved segments ─────────────────── */
const LS_KEY = "lead_segments_v1";
function loadSavedSegments() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
}
function saveSavedSegments(arr) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch {}
}

/* ── Filter-condition evaluator ──────────────────────────────── */
function _rawEvalCondition(lead, c) {
  const score = getLeadScore(lead);
  // Iter 188.2 — "never contacted" support. Leads that were never
  // called/emailed have `last_contact_date` null and `days_stale`
  // defaulted to 0 on the server. Treat that state as `Infinity`
  // days-stale so the AI parser's sentinel range (min=9999, max=99999)
  // correctly matches. A real 0-day stale value is only produced by an
  // outreach that literally happened today, which sets last_contact_date.
  const hasContact = !!(lead.last_contact_date && String(lead.last_contact_date).trim());
  const days = hasContact ? (Number(lead.days_stale) || 0) : Infinity;
  const v = lead;
  switch (c.field) {
    case "practice_type": return Array.isArray(c.value) ? c.value.includes(derivePracticeType(v)) : true;
    case "size_band":     return Array.isArray(c.value) ? c.value.includes(deriveSizeBand(v)) : true;
    case "stage":     return Array.isArray(c.value) ? c.value.some((x) => (v.stage || "").includes(x)) : true;
    case "specialty": {
      // Iter 188.4 (Feb 2026 — 2nd pass) — anchored whole-field
      // compare that MIRRORS the backend Lumina query at
      // services/lumina_leads.py:224-230:
      //   { $or: [
      //       { specialty: /^<term>$/i },
      //       { $and: [
      //           { $or: [{ specialty: {$in:[null,""]}}, { specialty: {$exists:false}}] },
      //           { $or: [{ "contacts.specialty": /^<term>$/i },
      //                   { "contacts.specialty_group": /^<term>$/i } ] }
      //       ]}
      //   ]}
      // Rules:
      //  - No comma/slash tokenization (previous "Internal Medicine,
      //    Nephrology" leaked in and inflated 263 → 447).
      //  - `contacts[]` is a FALLBACK — only consulted when the
      //    practice-level `specialty` is null / empty / missing.
      //  - `nppes.specialty_group` is NEVER consulted (backend does
      //    not query it).
      if (!Array.isArray(c.value) || !c.value.length) return true;
      const norm = (s) => String(s || "").trim().toLowerCase();
      const wanted = c.value.map(norm).filter(Boolean);
      if (!wanted.length) return true;
      const own = norm(v.specialty);
      if (own) return wanted.includes(own);
      const contacts = Array.isArray(v.contacts) ? v.contacts : [];
      return contacts.some(
        (cc) => wanted.includes(norm(cc?.specialty)) || wanted.includes(norm(cc?.specialty_group))
      );
    }
    case "region":    return Array.isArray(c.value) ? c.value.includes(v.region) : true;
    case "state":     return Array.isArray(c.value) ? c.value.includes(v.state || v.practice?.state) : true;
    // Iter 188.4 (Feb 2026) — lead_source field so AI-parsed
    // "leads from Ampliz" segments are re-evaluable in the UI.
    case "lead_source": {
      if (!Array.isArray(c.value) || !c.value.length) return true;
      const src = String(v.lead_source || v.source || "").toLowerCase().trim();
      if (!src) return false;
      return c.value.some((x) => String(x).toLowerCase().trim() === src);
    }
    // Iter 140.8 (Peter 49b1eece) — City + NPPES taxonomy parity.
    case "city": {
      if (!Array.isArray(c.value) || !c.value.length) return true;
      const cityVal = String(v.city || v.practice?.city || v.address?.city || "").toLowerCase().trim();
      if (!cityVal) return false;
      return c.value.some((x) => cityVal === String(x).toLowerCase().trim());
    }
    case "nppes_taxonomy": {
      if (!Array.isArray(c.value) || !c.value.length) return true;
      const codes = [
        v.nppes?.primary_taxonomy,
        v.nppes?.taxonomy_code,
        v.nppes?.taxonomy,
        ...(Array.isArray(v.nppes?.taxonomies) ? v.nppes.taxonomies.map((t) => t?.code || t) : []),
        v.taxonomy_code,
      ].filter(Boolean).map((s) => String(s).toLowerCase().trim());
      return c.value.some((x) => codes.includes(String(x).toLowerCase().trim()));
    }
    case "ai_score":  return (score >= (c.min || 0)) && (score <= (c.max || 100));
    case "rating":    return Array.isArray(c.value) ? c.value.includes(v.rating || tempLabel(score)) : true;
    case "last_contact_days": {
      const cMin = c.min || 0;
      const cMax = Number.isFinite(c.max) ? c.max : 99999;
      // Iter 188.2 — never-contacted match: days=Infinity satisfies any
      // sentinel range whose min is well above realistic call history
      // (>= 9000d ≈ 24y). This matches Lumina's `_never_called` sentinel
      // and segment-parse's `last_contact_days min=9999,max=99999`.
      if (!Number.isFinite(days)) return cMin >= 9000;
      return days >= cMin && days <= cMax;
    }
    case "ehr_vendor": return Array.isArray(c.value) ? c.value.some((x) => (v.ehr?.vendor || v.ehr_vendor || "(none)").toLowerCase().includes(x.toLowerCase())) : true;
    case "medicare_benes": { const b = v.medicare?.beneficiaries || v.medicare_benes || 0; return b >= (c.min || 0) && b <= (c.max || 1e9); }
    case "deal_value": { const dv = v.value || v.deal_value || 0; return dv >= (c.min || 0) && dv <= (c.max || 1e12); }
    case "product_fit": {
      if (!Array.isArray(c.value) || !c.value.length) return true;
      try { const sc = scoreProducts(v); return c.value.includes(sc?.primary?.product); } catch { return true; }
    }
    default:
      // Iter 139.3: extended Lead-Focus fields (organizational_role,
      // practice_type, lead_tag, feature_interest, …) fall through here.
      // The evaluator does a multi-check intersect against the lead
      // doc's actual stored shape (string or array) using the
      // EXTENDED_FILTER_FIELD_MAP metadata as a hint.
      if (EXTENDED_FILTER_FIELD_MAP[c.field]) {
        return evalExtendedCondition(v, c.field, c.value);
      }
      return true;
  }
}

/**
 * Iter 173 (Danny — AI segment include/exclude):
 * Public eval wrapper that honors the per-condition `negate` flag.
 * When `c.negate === true`, the underlying match result is flipped so
 * the row functions as an EXCLUDE clause (rep can say "Cardiologists
 * in NY but NOT in NYC" → 2 rows: state=NY, city=NYC with negate=true).
 *
 * Important: an empty-value row (`c.value=[]`) returns true from the
 * raw evaluator, so negating it would suddenly exclude every lead.
 * We treat empty-value negate rows as no-ops to avoid that footgun.
 */
function evalCondition(lead, c) {
  if (!c) return true;
  const raw = _rawEvalCondition(lead, c);
  if (!c.negate) return raw;
  // No-op guard: a negate on an empty/unset filter would exclude
  // every lead, which is never what the user intended.
  const hasValue =
    (Array.isArray(c.value) && c.value.length > 0) ||
    c.min != null ||
    c.max != null;
  if (!hasValue) return true;
  return !raw;
}
function evalSegment(lead, conditions, op = "AND") {
  if (!conditions?.length) return true;
  if (op === "OR") return conditions.some((c) => evalCondition(lead, c));
  return conditions.every((c) => evalCondition(lead, c));
}

// Iter 173 — exposed for jest tests of the include/exclude eval path.
// Not part of the public component API — internal helpers only.
export const __INTERNALS__ = { evalCondition, evalSegment, summarizeConditions };
function summarizeConditions(conditions) {
  if (!conditions?.length) return "No filters · all leads";
  return conditions.map((c) => {
    const f = FILTER_FIELDS.find((x) => x.id === c.field);
    const ext = EXTENDED_FILTER_FIELD_MAP[c.field];
    const lbl = f?.label || ext?.label || c.field;
    // Iter 173 — exclude rows render with a NOT prefix.
    const prefix = c.negate ? "NOT " : "";
    if (Array.isArray(c.value)) return `${prefix}${lbl}: ${c.value.join(", ") || "Any"}`;
    if (c.min != null || c.max != null) return `${prefix}${lbl}: ${c.min ?? "0"}–${c.max ?? "∞"}`;
    return `${prefix}${lbl}`;
  }).join(" · ");
}

/* ─────────────────────────────────────────────────────────────── */
export default function LeadSegments({ leads = [], user, currentRole = "super_admin" }) {
  const navigate = useNavigate();
  const dataset = leads;

  // Role gates
  const role = currentRole || "super_admin";
  const canSendMass = ["super_admin", "crm_admin", "sales_manager", "marketing_manager"].includes(role);
  const canCreateCampaign = ["super_admin", "crm_admin", "sales_manager"].includes(role);
  const canCreateTeamSeg = ["super_admin", "crm_admin", "sales_manager", "marketing_manager"].includes(role);
  const canExportCsv = canExport(role);
  // Backend handle for Ampliz export (separate from local CSV builder).
  const API   = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;

  // AI auto-generated segments — rebuilt every render off the dataset
  const aiSegments = useMemo(() => generateAISegments(dataset), [dataset]);
  const aiSegmentRefreshedAt = useMemo(() => new Date().toISOString(), [dataset]);

  // Saved segments persist to localStorage
  const [saved, setSaved] = useState(() => loadSavedSegments());
  const mySegments = saved.filter((s) => s.owner === "me");
  const teamSegments = saved.filter((s) => s.owner !== "me");
  const teamSegmentsWithDemo = teamSegments;

  // Active segment selection — default to first AI-generated segment with leads
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    if (!activeId && aiSegments.length) {
      const first = aiSegments.find((s) => s.count > 0) || aiSegments[0];
      setActiveId(first.id);
    } else if (!activeId) {
      setActiveId("qs-hot");
    }
  }, [activeId, aiSegments]);

  const allSegments = useMemo(() => {
    return [
      ...aiSegments.map((s) => ({ ...s, kind: "ai", type: "live" })),
      ...QUICK_STARTS.map((q) => ({ ...q, kind: "quick", type: "live", conditions: [] })),
      ...mySegments.map((s) => ({ ...s, kind: "mine" })),
      ...teamSegmentsWithDemo.map((s) => ({ ...s, kind: "team" })),
    ];
  }, [aiSegments, mySegments, teamSegmentsWithDemo]);

  const activeSeg = allSegments.find((s) => s.id === activeId) || aiSegments[0] || QUICK_STARTS[0];

  // Compute matching leads for any segment
  const computeMatches = useCallback((seg) => {
    if (!seg) return [];
    if (seg.kind === "ai") return seg.leads || [];
    if (seg.kind === "quick") return dataset.filter(seg.filter);
    // Iter 139: Lumina-pinned segments hold a frozen ID list. Use it
    // when present so saved "pulmonologists with COPD…" style segments
    // surface the real Lumina audience instead of zeroing out under
    // structured re-evaluation.
    if (Array.isArray(seg.luminaPinIds) && seg.luminaPinIds.length) {
      const pin = new Set(seg.luminaPinIds);
      return dataset.filter((l) => pin.has(l.id || l.npi));
    }
    return dataset.filter((l) => evalSegment(l, seg.conditions, seg.op || "AND"));
  }, [dataset]);

  const matches = useMemo(() => computeMatches(activeSeg), [activeSeg, computeMatches]);

  // Sort + selection
  const [sortBy, setSortBy] = useState("ai_score");
  const sortedMatches = useMemo(() => {
    const arr = [...matches];
    arr.sort((a, b) => {
      const A = a, B = b;
      switch (sortBy) {
        case "rating":      return (getLeadScore(B) - getLeadScore(A));
        case "last_contact": return ((A.days_stale || 0) - (B.days_stale || 0));
        case "name":        return ((A.clinic || A.title || "").localeCompare(B.clinic || B.title || ""));
        case "value":       return ((B.value || B.deal_value || 0) - (A.value || A.deal_value || 0));
        case "ai_score":
        default:            return (getLeadScore(B) - getLeadScore(A));
      }
    });
    return arr;
  }, [matches, sortBy]);
  const [selected, setSelected] = useState({});
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  useEffect(() => { setSelected({}); }, [activeId]);

  // Builder slide-in
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderEdit, setBuilderEdit] = useState(null); // null = create

  // Modals
  const [emailOpen, setEmailOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  // Stats for the active segment
  const stats = useMemo(() => {
    const hot = matches.filter((l) => (getLeadScore(l)) >= 71).length;
    const warm = matches.filter((l) => { const s = getLeadScore(l); return s >= 41 && s < 71; }).length;
    const avg = matches.length ? Math.round(matches.reduce((s, l) => s + (getLeadScore(l)), 0) / matches.length) : 0;
    return { total: matches.length, hot, warm, avg };
  }, [matches]);

  // Build/save handlers
  const onSaveSegment = (seg) => {
    const next = { ...seg, id: seg.id || `seg-${Date.now()}`, owner: seg.shareTeam && canCreateTeamSeg ? "Team" : "me", createdAt: new Date().toISOString() };
    const idx = saved.findIndex((s) => s.id === next.id);
    const arr = idx >= 0 ? saved.map((s, i) => i === idx ? next : s) : [...saved, next];
    setSaved(arr);
    saveSavedSegments(arr);
    setActiveId(next.id);
    setBuilderOpen(false);
    toast.success(`Segment saved · ${next.name}`);
  };
  const onDeleteSegment = (id) => {
    const arr = saved.filter((s) => s.id !== id);
    setSaved(arr);
    saveSavedSegments(arr);
    if (activeId === id) setActiveId("qs-hot");
    setBuilderOpen(false);
    toast.success("Segment deleted");
  };

  // Filter pills bar — derive from active segment conditions
  const conditionPills = activeSeg?.conditions || [];

  return (
    <div data-testid="lead-segments-page" style={{ display: "flex", gap: 0, height: "calc(150vh + 150px)", overflow: "hidden", border: "0.5px solid #eeeeee", borderRadius: 14, background: "#fff", boxShadow: "0 1px 0 rgba(0,0,0,0.02), 0 8px 24px -16px rgba(0,0,0,0.08)" }}>
      {/* ── Left library panel ─────────────────────────────── */}
      {/* Iter 320.8 (Feb 2026) — root scaled from the 2× (`200vh +
          200px`) to a 1.5× (`150vh + 150px`) at user's request.
          Everything else about the layout is unchanged: `overflow:
          hidden` still caps the flex row, `flex:1` on the lead list
          still receives a bounded envelope, and its internal
          `overflowY: auto` still paginates all 50/100/150+ rows. */}
      <aside style={{ width: 240, flexShrink: 0, borderRight: "0.5px solid #eeeeee", display: "flex", flexDirection: "column", background: "linear-gradient(180deg, #FAFCFE 0%, #F5F9FF 100%)" }}>
        <div style={{ padding: "14px 14px 12px", borderBottom: "0.5px solid #eeeeee", background: "#fff" }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--navy)", letterSpacing: "-0.01em", marginBottom: 10 }}>Segments</div>
          <button
            data-testid="seg-new-btn"
            onClick={() => { setBuilderEdit(null); setBuilderOpen(true); }}
            style={{ width: "100%", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 1px 2px rgba(37,99,235,0.18)" }}
          >
            <Plus style={{ width: 14, height: 14 }} /> New segment
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 12 }}>
          <SegCategoryLabel>
            AI-generated · by product fit
            <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 500, color: "#3C3489", background: "#EEEDFE", borderRadius: 6, padding: "1px 6px" }}>refreshed {relTime(aiSegmentRefreshedAt)}</span>
          </SegCategoryLabel>
          {[...aiSegments].sort((a, b) => b.count - a.count).map((s) => (
            <SegRow
              key={s.id}
              active={activeId === s.id}
              dotColor={primaryDot(s.primary)}
              name={s.name}
              count={s.count}
              badge="✦ AI"
              tooltip={{ primary: s.primary, secondary: s.secondary, tagline: s.tagline }}
              onClick={() => setActiveId(s.id)}
              testid={`seg-row-${s.id}`}
            />
          ))}

          <SegCategoryLabel>Quick starts</SegCategoryLabel>
          {QUICK_STARTS.map((q) => (
            <SegRow
              key={q.id}
              active={activeId === q.id}
              dotColor={q.dot}
              name={q.name}
              count={dataset.filter(q.filter).length}
              onClick={() => setActiveId(q.id)}
              testid={`seg-row-${q.id}`}
            />
          ))}

          <SegCategoryLabel>My segments</SegCategoryLabel>
          {mySegments.length === 0 ? (
            <div style={{ fontSize: 11, color: "#888", padding: "6px 14px 10px", lineHeight: 1.5 }}>
              No segments yet — click <strong style={{ color: "#2563eb" }}>+ New segment</strong> to build your first.
            </div>
          ) : mySegments.map((s) => (
            <SegRow
              key={s.id}
              active={activeId === s.id}
              dotColor={s.color || "#2563eb"}
              name={s.name}
              count={computeMatches({ ...s, kind: "mine" }).length}
              onClick={() => setActiveId(s.id)}
              testid={`seg-row-${s.id}`}
            />
          ))}

          {teamSegmentsWithDemo.length > 0 && (
            <>
              <SegCategoryLabel>Team segments</SegCategoryLabel>
              {teamSegmentsWithDemo.map((s) => (
                <SegRow
                  key={s.id}
                  active={activeId === s.id}
                  dotColor={s.color || "#888"}
                  name={s.name}
                  badge={s.campaignLinked ? "Campaign" : null}
                  avatar={s.owner && s.owner !== "me" ? initials(s.owner) : null}
                  count={computeMatches({ ...s, kind: "team" }).length}
                  onClick={() => setActiveId(s.id)}
                  testid={`seg-row-${s.id}`}
                />
              ))}
            </>
          )}
        </div>
      </aside>

      {/* ── Right workspace ────────────────────────────────── */}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "#fff" }}>
        {/* Empty / no segment selected fallback */}
        {!activeSeg ? (
          <EmptyWorkspace onNew={() => { setBuilderEdit(null); setBuilderOpen(true); }} />
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #eeeeee", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h2 data-testid="seg-active-name" style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--navy)", letterSpacing: "-0.01em" }}>{activeSeg.name}</h2>
                  <Badge tone={activeSeg.type === "static" ? "gray" : "green"}>
                    {activeSeg.type === "static" ? "Static · fixed list" : "Live · auto-updates"}
                  </Badge>
                  {activeSeg.campaignLinked && <Badge tone="purple">Campaign</Badge>}
                  {activeSeg.kind === "quick" && <Badge tone="blue">System</Badge>}
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                  {activeSeg.kind === "quick"
                    ? "Pre-built system segment · auto-refreshes from live lead data"
                    : summarizeConditions(activeSeg.conditions)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {activeSeg.kind !== "quick" && !activeSeg.campaignLinked && (
                  <button
                    data-testid="seg-edit-filters"
                    onClick={() => { setBuilderEdit(activeSeg); setBuilderOpen(true); }}
                    style={btnStyle.outline}
                  >
                    <Edit3 style={{ width: 13, height: 13 }} /> Edit filters
                  </button>
                )}
                {canCreateCampaign && (
                  <button
                    data-testid="seg-start-campaign"
                    onClick={() => setLaunchOpen(true)}
                    style={btnStyle.purple}
                  >
                    <Play style={{ width: 13, height: 13 }} /> Start campaign
                  </button>
                )}
                {canSendMass && (
                  <button data-testid="seg-mass-email" onClick={() => setEmailOpen(true)} style={btnStyle.primary}>
                    <Mail style={{ width: 13, height: 13 }} /> Mass email
                  </button>
                )}
              </div>
            </div>

            {/* Stats bar */}
            <div style={{ display: "flex", borderBottom: "0.5px solid #eeeeee", background: "#fff" }}>
              <Stat label="Total leads" value={stats.total} />
              <Stat label="Hot" value={stats.hot} valueColor="#E24B4A" />
              <Stat label="Warm" value={stats.warm} valueColor="#BA7517" />
              <Stat label="Avg AI score" value={stats.avg} valueColor="#185FA5" />
              <Stat label="Last updated" value={relTime(activeSeg.createdAt)} small last />
            </div>

            {/* Filter pills */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, padding: "8px 16px", borderBottom: "0.5px solid #eeeeee", background: "#fff" }}>
              <span style={{ fontSize: 10, color: "#888", flexShrink: 0, letterSpacing: "0.04em", textTransform: "uppercase" }}>Filters:</span>
              {conditionPills.length === 0 ? (
                <span style={{ fontSize: 11, color: "#aaa" }}>{activeSeg.kind === "quick" ? "System-defined · not editable" : "No filters · all leads"}</span>
              ) : conditionPills.map((c, i) => {
                const f = FILTER_FIELDS.find((x) => x.id === c.field);
                const tone = TONE_PILL[f?.tone || "blue"];
                const label = Array.isArray(c.value) ? `${f?.label || c.field}: ${c.value.slice(0, 2).join(", ")}${c.value.length > 2 ? ` +${c.value.length - 2}` : ""}` : `${f?.label || c.field}: ${c.min ?? 0}–${c.max ?? "∞"}`;
                return (
                  <span key={i} data-testid={`seg-pill-${c.field}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: tone.bg, color: tone.color }}>
                    {label}
                    {activeSeg.kind !== "quick" && !activeSeg.campaignLinked && (
                      <X
                        style={{ width: 11, height: 11, opacity: 0.6, cursor: "pointer" }}
                        onClick={() => {
                          const next = { ...activeSeg, conditions: activeSeg.conditions.filter((_, j) => j !== i) };
                          onSaveSegment(next);
                        }}
                      />
                    )}
                  </span>
                );
              })}
              {activeSeg.kind !== "quick" && !activeSeg.campaignLinked && (
                <button data-testid="seg-add-filter" onClick={() => { setBuilderEdit(activeSeg); setBuilderOpen(true); }} style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                  + Add filter
                </button>
              )}
            </div>

            {/* AI Segment Panel — full Product-Fit Intelligence */}
            <ProductFitIntelligencePanel segment={activeSeg} matches={sortedMatches} />

            {/* Lead list header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", borderBottom: "0.5px solid #eeeeee", background: "#fafafa" }}>
              <span style={{ fontSize: 11, color: "#888" }}>
                Showing <strong style={{ color: "#222", fontWeight: 600 }}>{Math.min(sortedMatches.length, 50)}</strong> of {sortedMatches.length} · sorted by {sortBy.replace("_", " ")}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <select data-testid="seg-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontFamily: "inherit", background: "#fff", color: "#222" }}>
                  <option value="ai_score">AI score</option>
                  <option value="rating">Lead rating</option>
                  <option value="last_contact">Last contact</option>
                  <option value="name">Practice name</option>
                  <option value="value">Deal value</option>
                </select>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "#555", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    data-testid="seg-select-all"
                    checked={sortedMatches.length > 0 && sortedMatches.every((l) => selected[l.id || l.npi])}
                    onChange={(e) => {
                      const next = {};
                      if (e.target.checked) sortedMatches.forEach((l) => { next[l.id || l.npi] = true; });
                      setSelected(next);
                    }}
                    style={{ width: 13, height: 13, cursor: "pointer" }}
                  />
                  Select all
                </label>
              </div>
            </div>

            {/* Lead list — Iter 158 (Josh) issue reports #3 #42: ensure
                the list always gets a generous minimum height so it
                doesn't get squashed by the AI/stats panels above. */}
            <div style={{ flex: 1, minHeight: 380, overflowY: "auto", padding: "10px 14px", background: "linear-gradient(180deg, #fff 0%, #FAFBFD 100%)" }}>
              {sortedMatches.length === 0 ? (
                <NoLeadsEmpty onEdit={() => { setBuilderEdit(activeSeg); setBuilderOpen(true); }} />
              ) : sortedMatches.slice(0, 50).map((l) => (
                <LeadRow
                  key={l.id || l.npi}
                  lead={l}
                  selected={!!selected[l.id || l.npi]}
                  onToggle={() => setSelected((s) => ({ ...s, [l.id || l.npi]: !s[l.id || l.npi] }))}
                  onOpen={() => navigate(`/lead-focus/${l.id || l.npi}`)}
                  onCall={() => { toast.success(`Calling ${l.contact_name || l.clinic || "lead"}…`); navigate(`/dialer?leadId=${l.id || l.npi}`); }}
                  role={role}
                />
              ))}
            </div>

            {/* Bulk action bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderTop: "0.5px solid #eeeeee", background: "#fff" }}>
              <span style={{ fontSize: 11, color: "#888" }}>
                {selectedIds.length === 0 ? "Select leads above to take action." : <><strong style={{ color: "#2563eb", fontWeight: 600 }}>{selectedIds.length}</strong> selected</>}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {canSendMass && (
                  <>
                    <button
                      data-testid="seg-bulk-email"
                      disabled={selectedIds.length === 0}
                      onClick={() => setEmailOpen(true)}
                      style={{ ...btnStyle.softBlueOutline, opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? "pointer" : "not-allowed" }}
                    >
                      <Mail style={{ width: 13, height: 13 }} /> Mass email
                    </button>
                    <button
                      data-testid="seg-bulk-sms"
                      disabled={selectedIds.length === 0}
                      onClick={() => setSmsOpen(true)}
                      style={{ ...btnStyle.softGreenOutline, opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? "pointer" : "not-allowed" }}
                    >
                      <MessageSquare style={{ width: 13, height: 13 }} /> Mass SMS
                    </button>
                  </>
                )}
                <button
                  data-testid="seg-bulk-push"
                  disabled={selectedIds.length === 0}
                  onClick={() => setPushOpen(true)}
                  style={{ ...btnStyle.purple, opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? "pointer" : "not-allowed" }}
                >
                  <Play style={{ width: 13, height: 13 }} /> Push to campaign
                </button>
                {canExportCsv && (
                  <button
                    data-testid="seg-bulk-export"
                    onClick={() => {
                      const rows = selectedIds.length ? sortedMatches.filter((l) => selected[l.id || l.npi]) : sortedMatches;
                      const headers = ["clinic","contact_name","specialty","stage","ai_score"];
                      const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${(r[h] || r[h.replace("contact_","")] || "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
                      const blob = new Blob([csv], { type: "text/csv" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `segment_${activeSeg.id}_${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      toast.success(`Exported ${rows.length} leads`);
                    }}
                    style={btnStyle.outline}
                  >
                    <Download style={{ width: 13, height: 13 }} /> Export
                  </button>
                )}
                {canExportCsv && (
                  <button
                    data-testid="seg-export-ampliz"
                    title="Export Ampliz-sourced leads in this segment as a 115-col vendor CSV"
                    onClick={async () => {
                      try {
                        toast.info("Preparing Ampliz CSV…");
                        const res = await fetch(`${API}/leads/export?format=ampliz`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
                        const blob = await res.blob();
                        const count = res.headers.get("x-lead-count");
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `ampliz-segment-${new Date().toISOString().slice(0, 10)}.csv`;
                        a.click();
                        toast.success(`Exported ${count || "?"} Ampliz leads`);
                      } catch (err) {
                        toast.error(`Export failed — ${err.message || err}`);
                      }
                    }}
                    style={btnStyle.outline}
                  >
                    <Download style={{ width: 13, height: 13 }} /> Ampliz CSV
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </main>

      {builderOpen && (
        <SegmentBuilder
          initial={builderEdit}
          dataset={dataset}
          onClose={() => setBuilderOpen(false)}
          onSave={onSaveSegment}
          onDelete={onDeleteSegment}
          canCreateTeamSeg={canCreateTeamSeg}
        />
      )}

      {emailOpen && (
        <MassEmailModal
          segment={activeSeg}
          recipients={selectedIds.length ? sortedMatches.filter((l) => selected[l.id || l.npi]) : sortedMatches}
          onClose={() => setEmailOpen(false)}
        />
      )}
      {smsOpen && (
        <MassSmsModal
          segment={activeSeg}
          recipients={selectedIds.length ? sortedMatches.filter((l) => selected[l.id || l.npi]) : sortedMatches}
          onClose={() => setSmsOpen(false)}
        />
      )}
      {pushOpen && (
        <PushToCampaignModal
          segment={activeSeg}
          count={selectedIds.length || sortedMatches.length}
          canCreateCampaign={canCreateCampaign}
          onClose={() => setPushOpen(false)}
          onPushed={(campName) => { toast.success(`${selectedIds.length || sortedMatches.length} leads added to ${campName}`); setPushOpen(false); }}
        />
      )}
      <CampaignQuickLaunchLightbox
        open={launchOpen}
        segment={activeSeg}
        leadCount={sortedMatches.length}
        segmentLeads={sortedMatches}
        onClose={() => setLaunchOpen(false)}
        onLaunch={async (payload) => {
          setLaunchOpen(false);
          // BW-01 (Feb 12 PM-5) — persist the quick-launch payload server-side.
          // We optimistically navigate to the dialer no matter what so reps
          // aren't blocked by a network hiccup. Toast shows the real campaign
          // id on success; failure surfaces as a warning toast but the dialer
          // still opens with the local payload (campaign just won't appear in
          // the campaigns list until they retry).
          const qs = new URLSearchParams({
            from_segment: activeSeg?.id || "",
            campaign_name: payload.name,
            script_id: payload.script_id,
            assign_mode: payload.assignMode,
            agents: payload.agentIds.join(","),
          });
          navigate(`/dialer?${qs.toString()}`);
          try {
            const axios = (await import("axios")).default;
            const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
            const res = await axios.post(`${API}/campaigns/quick-launch`, payload, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
              timeout: 8000,
            });
            const cid = res?.data?.id;
            qs.set("campaign_id", cid || "");
            toast.success(`Campaign "${payload.name}" launched${cid ? ` · ID ${cid.slice(0, 8)}…` : ""} · ${payload.agentIds.length} agent${payload.agentIds.length === 1 ? "" : "s"} · ${payload.leadCount} leads`);
          } catch (e) {
            console.warn("[quick-launch] backend persist failed:", e?.response?.status, e?.message);
            toast.warning(`Campaign queued locally — server save failed (${e?.response?.status || "network"})`);
          }
        }}
      />
    </div>
  );
}

/* ── Helper: dot colour for primary product ─────────────── */
function primaryDot(p) {
  return ({ EHR: "#185FA5", PM: "#534AB7", RCM: "#0F6E56", APCM: "#3C3489", CCM: "#1D9E75", RPM: "#378ADD", AWV: "#BA7517" }[p]) || "#888";
}

/* ── ProductFitIntelligencePanel ─────────────────────────
 * Per the AI Lead-Targeting Spec & Specialty × Product Matrix.
 * Shows per-product fit cards with CMS gates, CCM-first overlay,
 * dollar revenue model from CMS_2026_RATES, and a Claude deep
 * analysis sub-panel.
 */
function ProductFitIntelligencePanel({ segment, matches }) {
  const [expanded, setExpanded] = useState(false);
  const [deep, setDeep] = useState(null);
  const [loading, setLoading] = useState(false);
  const agg = useMemo(() => aggregateSegment(matches), [matches]);
  if (!agg) return null;

  // Re-rank — for AI segments, we know the primary; otherwise highest avg
  const primaryProduct = segment?.primary || agg.ranked[0]?.product;
  const ranked = [...agg.ranked].sort((a, b) => {
    if (a.product === primaryProduct) return -1;
    if (b.product === primaryProduct) return 1;
    return b.score - a.score;
  });

  const apcmEligible = segment?.apcm_eligible !== false && agg.apcmIneligibleCount < matches.length / 2;
  const apcmOverflow = segment?.apcm_overflow || (ranked.find((r) => r.product === "CCM")?.score >= 50 && ranked.find((r) => r.product === "APCM")?.score >= 50);

  // Revenue model (from CMS 2026 rates)
  const ccmEnrolled = Math.round(agg.totalBenes * CMS_2026_RATES.CCM.enrollment_assumption);
  const apcmEnrolled = Math.round(agg.totalBenes * CMS_2026_RATES.APCM.enrollment_assumption);
  const rpmEnrolled  = Math.round(agg.totalBenes * CMS_2026_RATES.RPM.enrollment_assumption);
  const ccmMo = Math.round(ccmEnrolled * CMS_2026_RATES.CCM.per_pt_per_mo / 1000);
  const rpmMo = Math.round(rpmEnrolled * CMS_2026_RATES.RPM.per_pt_per_mo / 1000);
  const apcmMo = apcmEligible ? Math.round(apcmEnrolled * CMS_2026_RATES.APCM.per_pt_per_mo / 1000) : 0;

  const fetchDeep = async () => {
    if (!matches?.[0] || loading) return;
    setLoading(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const axios = (await import("axios")).default;
      const repLead = matches[0];
      const repScoring = scoreProducts(repLead);
      const compStub = competitiveAnalysis(repLead, repScoring);
      const res = await axios.post(
        `${BACKEND}/api/ai/deep-competitive-analysis`,
        { lead: { ...repLead, clinic: `${segment?.name} segment (${matches.length} practices)` }, scoring: repScoring, competitive: { ...(compStub || {}), incumbent: agg.topVendors?.[0]?.[0] || "Mixed incumbents" } },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setDeep(res.data);
    } catch {} finally { setLoading(false); }
  };

  const drugSignals = agg.topDrugs.length ? agg.topDrugs.map((d) => d[0]).join(" + ") : "—";
  const topVendor = agg.topVendors?.[0];
  const topVendorBucket = topVendor ? vendorBucket(topVendor[0]) : null;

  return (
    <div data-testid="seg-ai-panel" style={{ borderBottom: "0.5px solid #eeeeee", background: "linear-gradient(135deg, #0a1f44 0%, #1b365D 50%, #185FA5 100%)", color: "#fff", padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.16)", border: "0.5px solid rgba(255,255,255,0.24)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Sparkles style={{ width: 16, height: 16, color: "#fff" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>Claude AI · Product-fit intelligence</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.78)" }}>{matches.length} practices · {segment?.tagline || "Per spec rules"}</span>
          </div>

          {/* 4 product fit cards */}
          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            {ranked.slice(0, 4).map((r, i) => {
              const isPrimary = i === 0;
              const isCmsBlocked = ((r.product === "APCM" || r.product === "AWV") && !apcmEligible);
              const score = isCmsBlocked ? 0 : r.score;
              const scoreColor = isCmsBlocked ? "#9ca3af" : score >= 75 ? "#9CECCB" : score >= 50 ? "#FFC97A" : "#cbd5e0";
              const diff = difficultyClass(r.product);
              const label = isPrimary ? `Primary fit · ${r.primaryFor || matches.length} of ${matches.length}` : i === 1 ? "Secondary · cross-sell" : i === 2 ? "Tertiary fit" : `${r.product} eligibility`;
              return (
                <div key={r.product} title={isCmsBlocked ? "APCM is restricted to primary care providers by CMS definition. Pitching APCM to this specialty will damage rep credibility." : undefined} style={{ borderRadius: 10, padding: "10px 12px", border: isPrimary ? "1.5px solid #5BD49C" : "0.5px solid rgba(255,255,255,0.18)", background: isPrimary ? "rgba(91,212,156,0.10)" : "rgba(255,255,255,0.08)" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{PRODUCT_NAMES[r.product]}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: scoreColor, marginTop: 4 }}>
                    {score} <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 400 }}>/ 100</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.10)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${score}%`, background: scoreColor, borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 5 }}>
                    {isCmsBlocked ? "✗ CMS ineligible" : `● ${diff?.label} sell`}
                    {r.product === "APCM" && apcmOverflow && !isCmsBlocked && " · overflow"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CMS concurrency warning */}
          {(apcmOverflow || !apcmEligible) && (
            <div style={{ marginTop: 8, padding: "5px 10px", borderRadius: 6, background: "rgba(245,158,11,0.18)", border: "0.5px solid rgba(245,158,11,0.35)", fontSize: 11, color: "#FCD34D", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <AlertTriangleIcon /> CMS: {!apcmEligible ? "APCM ineligible for this specialty — never pitch APCM here." : "CCM and APCM cannot be billed same month same patient — APCM is overflow only."}
            </div>
          )}

          {/* Revenue headline */}
          <div style={{ marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.78)" }}>
            Est. CCM at {Math.round(CMS_2026_RATES.CCM.enrollment_assumption*100)}% enrollment: <strong style={{ color: "#fff" }}>${ccmMo}K/mo</strong>
            {" · "}RPM add-on ({rpmEnrolled.toLocaleString()} enrolled): <strong style={{ color: "#fff" }}>${rpmMo}K/mo</strong>
            {apcmEligible && <> {" · "}APCM overflow: <strong style={{ color: "#fff" }}>${apcmMo}K/mo</strong></>}
            {" · "}Combined: <strong style={{ color: "#9CECCB" }}>${ccmMo + rpmMo + apcmMo}K/mo</strong>
          </div>

          {/* AI insight body */}
          <div style={{ marginTop: 12, padding: "10px 12px", background: "#0F2942", borderRadius: 10, border: "0.5px solid rgba(255,255,255,0.15)" }}>
            <div style={{ fontSize: 11, color: "#E2E8F0", lineHeight: 1.7 }}>
              <p style={{ margin: 0, marginBottom: 6 }}><strong style={{ color: "#fff" }}>Segment summary:</strong> {matches.length} {(segment?.name || "").includes("—") ? segment.name.split("—")[1].trim().toLowerCase() : "practices"} confirmed. CMS eligibility: <strong style={{ color: "#9CECCB" }}>CCM ✔</strong> <strong style={{ color: "#9CECCB" }}>RPM ✔</strong> {apcmEligible ? <strong style={{ color: "#9CECCB" }}>APCM ✔</strong> : <strong style={{ color: "#FCA5A5" }}>APCM ✗ (specialist gate)</strong>}{!apcmEligible && <span style={{ color: "#FCD34D" }}> · do not pitch APCM</span>}.</p>
              <p style={{ margin: 0, marginBottom: 6 }}><strong style={{ color: "#fff" }}>Clinical signals:</strong> Top drug signals: <em style={{ color: "#FFD8A8" }}>{drugSignals}</em> → confirms {ranked[1]?.product || "RPM"} device fit.</p>
              <p style={{ margin: 0, marginBottom: 6 }}><strong style={{ color: "#fff" }}>Competitive intelligence:</strong> {topVendor ? <>{topVendor[1]} of {matches.length} on <em>{topVendor[0]}</em> ({topVendorBucket?.label}). Strategy: <strong style={{ color: "#9CECCB" }}>{topVendorBucket?.strategy}</strong>. {topVendorBucket?.talking_point}</> : "Mixed incumbents — discovery required."}</p>
              <p style={{ margin: 0 }}><strong style={{ color: "#fff" }}>Revenue frame:</strong> Pitch {PRODUCT_NAMES[primaryProduct]} ({CMS_2026_RATES[primaryProduct]?.code || "—"}) at ${CMS_2026_RATES[primaryProduct]?.per_pt_per_mo || CMS_2026_RATES[primaryProduct]?.per_visit || "—"}/{CMS_2026_RATES[primaryProduct]?.per_visit ? "visit" : "pt/mo"}. Layer {ranked[1]?.product || "RPM"} on {Math.round(matches.length * 0.6)}+ practices → est. <strong style={{ color: "#9CECCB" }}>${ccmMo + rpmMo}K/mo</strong> combined.</p>
            </div>
          </div>

          {/* Data pills */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            <DataPill>Avg AI {Math.round(matches.reduce((s, l) => s + (getLeadScore(l)), 0) / matches.length)}</DataPill>
            <DataPill>Avg benes {Math.round(agg.totalBenes / matches.length).toLocaleString()}</DataPill>
            <DataPill>Telehealth Y · {agg.telehealthPct}%</DataPill>
            <DataPill>Top states · {agg.topStates.map((s) => `${s[0]} (${s[1]})`).join(", ") || "—"}</DataPill>
            <DataPill>Incumbent · {agg.topVendors.map((v) => `${v[0]} (${v[1]})`).join(", ") || "—"}</DataPill>
            {agg.dualEligibleTotal > 0 && <DataPill>Dual-eligible · {agg.dualEligibleTotal.toLocaleString()} ($117/mo G0558)</DataPill>}
          </div>

          {/* Deep analysis button + body */}
          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button data-testid="seg-ai-expand" onClick={() => { setExpanded((x) => !x); if (!deep && !loading) fetchDeep(); }} style={{ background: "rgba(255,255,255,0.12)", color: "#CBD5E0", border: "0.5px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {loading ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Sparkles style={{ width: 12, height: 12 }} />}
              Deep analysis →
            </button>
          </div>

          {expanded && (
            <div style={{ marginTop: 12, background: "rgba(255,255,255,0.10)", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 10, padding: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Per-product avg score</div>
                  {ranked.slice(0, 7).map((r) => {
                    const blocked = (r.product === "APCM" || r.product === "AWV") && !apcmEligible;
                    return (
                      <div key={r.product} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: blocked ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.92)", padding: "3px 0", borderBottom: "0.5px solid rgba(255,255,255,0.10)" }}>
                        <span>{PRODUCT_NAMES[r.product]}{blocked && " ✗"}</span><span style={{ fontWeight: 600 }}>{blocked ? "0" : r.score}</span>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>EHR vendor mix</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.7 }}>
                    Strong incumbent · <strong>{agg.ehrBuckets.STRONG_INCUMBENT}</strong><br />
                    Mid-tier · <strong>{agg.ehrBuckets.MID_TIER}</strong><br />
                    Legacy / sunset · <strong>{agg.ehrBuckets.WEAKENING_LEGACY}</strong><br />
                    Greenfield (no EHR) · <strong>{agg.ehrBuckets.NO_EHR}</strong>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Revenue cap (12 mo)</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.7 }}>
                    CCM &nbsp;<strong>${(ccmMo*12).toLocaleString()}K</strong><br />
                    RPM &nbsp;<strong>${(rpmMo*12).toLocaleString()}K</strong><br />
                    {apcmEligible && <>APCM <strong>${(apcmMo*12).toLocaleString()}K</strong><br /></>}
                    AWV &nbsp;<strong>${Math.round(agg.totalBenes * CMS_2026_RATES.AWV.capture_assumption * CMS_2026_RATES.AWV.per_visit / 1000).toLocaleString()}K</strong>
                  </div>
                </div>
              </div>
              {loading && <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.75)" }}><Loader2 style={{ width: 12, height: 12, display: "inline-block", verticalAlign: "middle" }} className="animate-spin" /> Claude is researching this segment…</div>}
              {deep && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.18)" }}>
                  {deep.deep_competitive?.market_position && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.95)", lineHeight: 1.55, marginBottom: 8 }}>{deep.deep_competitive.market_position}</div>}
                  {deep.objection_handlers?.[0] && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.55 }}><strong style={{ color: "#fff" }}>"{deep.objection_handlers[0].objection}"</strong> — {deep.objection_handlers[0].response}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DataPill({ children }) { return <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.85)", border: "0.5px solid rgba(255,255,255,0.18)" }}>{children}</span>; }
function AlertTriangleIcon() { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>; }

/* ── SegmentAIPanel — legacy aggregate (kept for fallback) ── */
function SegmentAIPanel({ segment, matches }) {
  const [expanded, setExpanded] = useState(false);
  const [deep, setDeep] = useState(null);
  const [loading, setLoading] = useState(false);

  // Aggregate scoring across all matched leads
  const aggregate = useMemo(() => {
    if (!matches?.length) return null;
    const productScores = {}; // product → cumulative score
    const productCounts = {}; // product → primary-fit count
    const vendors = {};
    const sizeBands = {};
    const practiceTypes = {};
    const states = {};
    let totalBenes = 0;
    matches.forEach((l) => {
      try {
        const sc = scoreProducts(l);
        sc.ranked.forEach((r) => { productScores[r.product] = (productScores[r.product] || 0) + r.score; });
        if (sc.primary) productCounts[sc.primary.product] = (productCounts[sc.primary.product] || 0) + 1;
      } catch {}
      const v = l.ehr?.vendor || "(none)";
      vendors[v] = (vendors[v] || 0) + 1;
      const sb = deriveSizeBand(l); sizeBands[sb] = (sizeBands[sb] || 0) + 1;
      const pt = derivePracticeType(l); practiceTypes[pt] = (practiceTypes[pt] || 0) + 1;
      const st = l.state || l.practice?.state; if (st) states[st] = (states[st] || 0) + 1;
      totalBenes += (l.medicare?.beneficiaries || l.medicare_benes || 0);
    });
    const ranked = Object.entries(productScores)
      .map(([p, s]) => ({ product: p, score: Math.round(s / matches.length), primaryFor: productCounts[p] || 0 }))
      .sort((a, b) => b.score - a.score);
    const topVendor = Object.entries(vendors).sort((a, b) => b[1] - a[1])[0];
    const topSize = Object.entries(sizeBands).sort((a, b) => b[1] - a[1])[0];
    const topPType = Object.entries(practiceTypes).sort((a, b) => b[1] - a[1])[0];
    const topStates = Object.entries(states).sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { ranked, topVendor, topSize, topPType, topStates, totalBenes };
  }, [matches]);

  if (!aggregate) {
    return null;
  }
  const top1 = aggregate.ranked[0];
  const top2 = aggregate.ranked[1];
  const top3 = aggregate.ranked[2];
  const apcmArr = Math.round(aggregate.totalBenes * 98 * 12 / 1000);
  const ccmArr  = Math.round(aggregate.totalBenes * 64 * 12 / 1000);
  const rpmArr  = Math.round(aggregate.totalBenes * 65 * 12 / 1000);

  // Pick a representative lead for Claude deep analysis (highest scoring match)
  const repLead = matches[0];
  const repScoring = repLead ? (() => { try { return scoreProducts(repLead); } catch { return null; } })() : null;
  const repComp = (repLead && repScoring) ? (() => { try { return competitiveAnalysis(repLead, repScoring); } catch { return null; } })() : null;

  const fetchDeep = async () => {
    if (!repLead || loading) return;
    setLoading(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const axios = (await import("axios")).default;
      const res = await axios.post(
        `${BACKEND}/api/ai/deep-competitive-analysis`,
        {
          lead: { ...repLead, clinic: `${segment?.name} segment (${matches.length} practices)` },
          scoring: repScoring,
          competitive: { ...(repComp || {}), incumbent: aggregate.topVendor?.[0] || "Mixed incumbents" },
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setDeep(res.data);
    } catch (e) {
      console.warn("segment deep AI failed", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="seg-ai-panel" style={{ borderBottom: "0.5px solid #eeeeee", background: "linear-gradient(135deg, #0a1f44 0%, #1b365D 50%, #185FA5 100%)", color: "#fff", padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.16)", border: "0.5px solid rgba(255,255,255,0.24)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Sparkles style={{ width: 16, height: 16, color: "#fff" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>Claude AI</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Segment recommendation</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.78)" }}>· {matches.length} practices · {aggregate.topPType?.[0]} · {aggregate.topSize?.[0]}</span>
          </div>
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
            <PanelCard title="Best-fit product" big>
              <strong style={{ color: "#fff" }}>{PRODUCT_NAMES[top1.product]}</strong>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
                {top1.primaryFor}/{matches.length} practices · {difficultyClass(top1.product)?.label || ""} sell · avg fit {top1.score}/100
              </div>
            </PanelCard>
            {top2 && (
              <PanelCard title="Secondary fit">
                <strong style={{ color: "#fff" }}>{PRODUCT_NAMES[top2.product]}</strong>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{top2.primaryFor} primary · avg {top2.score}</div>
              </PanelCard>
            )}
            <PanelCard title="Incumbent EHR">
              <strong style={{ color: "#fff" }}>{aggregate.topVendor?.[0] || "—"}</strong>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{aggregate.topVendor?.[1] || 0} of {matches.length}</div>
            </PanelCard>
            <PanelCard title="APCM revenue lift">
              <strong style={{ color: "#fff" }}>${apcmArr}K/yr</strong>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{aggregate.totalBenes.toLocaleString()} Medicare benes</div>
            </PanelCard>
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button data-testid="seg-ai-expand" onClick={() => { setExpanded((x) => !x); if (!deep && !loading) fetchDeep(); }} style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.28)", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {loading ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Zap style={{ width: 12, height: 12 }} />}
              {expanded ? "Hide deep analysis" : "Deep analysis with Claude"}
            </button>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.65)" }}>Top states: {aggregate.topStates.map((s) => `${s[0]} (${s[1]})`).join(" · ") || "—"}</span>
          </div>

          {expanded && (
            <div style={{ marginTop: 12, background: "rgba(255,255,255,0.10)", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 10, padding: 12 }}>
              {/* Always-on spec-driven analysis */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Product fit ranking</div>
                  {aggregate.ranked.slice(0, 5).map((r) => (
                    <div key={r.product} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.92)", padding: "3px 0", borderBottom: "0.5px solid rgba(255,255,255,0.10)" }}>
                      <span>{PRODUCT_NAMES[r.product]}</span><span style={{ fontWeight: 600 }}>{r.score}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Revenue model</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.7 }}>
                    APCM &nbsp;<strong>${apcmArr}K/yr</strong><br />
                    CCM &nbsp;&nbsp;<strong>${ccmArr}K/yr</strong><br />
                    RPM &nbsp;&nbsp;<strong>${rpmArr}K/yr</strong><br />
                    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 10 }}>Combined cap @ ~70% adoption: <strong style={{ color: "#fff" }}>${Math.round((apcmArr + ccmArr + rpmArr) * 0.7).toLocaleString()}K/yr</strong></span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Segment composition</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.7 }}>
                    {aggregate.topPType?.[0]}: <strong>{Math.round((aggregate.topPType?.[1] / matches.length) * 100)}%</strong><br />
                    {aggregate.topSize?.[0]}: <strong>{Math.round((aggregate.topSize?.[1] / matches.length) * 100)}%</strong><br />
                    Avg benes: <strong>{Math.round(aggregate.totalBenes / matches.length).toLocaleString()}</strong>
                  </div>
                </div>
              </div>
              {/* Claude deep analysis */}
              {loading && (
                <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", gap: 8 }}>
                  <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> Claude is researching this segment…
                </div>
              )}
              {deep && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.18)" }}>
                  {deep.deep_competitive?.market_position && (
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.95)", lineHeight: 1.55, marginBottom: 8 }}>{deep.deep_competitive.market_position}</div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {deep.deep_competitive?.win_themes?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Win themes</div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.55 }}>
                          {deep.deep_competitive.win_themes.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                    )}
                    {deep.objection_handlers?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Top objection</div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.92)", lineHeight: 1.55 }}>
                          <strong style={{ color: "#fff" }}>"{deep.objection_handlers[0].objection}"</strong>
                          <div style={{ marginTop: 2 }}>{deep.objection_handlers[0].response}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PanelCard({ title, children, big }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.10)", border: "0.5px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "8px 12px" }}>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: big ? 13 : 12, color: "#fff", fontWeight: 600 }}>{children}</div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function SegCategoryLabel({ children }) {
  return (
    <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.06em", fontWeight: 600, padding: "12px 14px 4px" }}>
      {children}
    </div>
  );
}

function SegRow({ active, dotColor, name, count, onClick, testid, badge, avatar }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "7px 10px", borderRadius: 8, margin: "0 6px 2px",
        cursor: "pointer", fontSize: 12,
        background: active ? "#E6F1FB" : "transparent",
        color: active ? "#185FA5" : "#222",
        fontWeight: active ? 600 : 400,
        transition: "background 120ms ease",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(0,0,0,0.03)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {avatar ? (
        <span style={{ width: 18, height: 18, borderRadius: 99, background: "#EEEDFE", color: "#3C3489", fontSize: 9, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{avatar}</span>
      ) : (
        <span style={{ width: 8, height: 8, borderRadius: 99, background: dotColor, flexShrink: 0 }} />
      )}
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      {badge && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "#EEEDFE", color: "#3C3489", fontWeight: 600 }}>{badge}</span>}
      <span style={{ marginLeft: "auto", fontSize: 10, padding: "1px 6px", borderRadius: 10, background: active ? "rgba(37,99,235,0.12)" : "#f5f5f5", color: active ? "#185FA5" : "#888", fontWeight: 600, flexShrink: 0 }}>{count}</span>
    </div>
  );
}

function Badge({ tone, children }) {
  const map = {
    green:  { bg: "#EAF3DE", c: "#3B6D11" },
    gray:   { bg: "#f0f0f0", c: "#666" },
    blue:   { bg: "#E6F1FB", c: "#185FA5" },
    purple: { bg: "#EEEDFE", c: "#3C3489" },
  };
  const s = map[tone] || map.gray;
  return <span style={{ background: s.bg, color: s.c, borderRadius: 10, fontSize: 10, fontWeight: 500, padding: "2px 8px" }}>{children}</span>;
}

function Stat({ label, value, valueColor, small, last }) {
  return (
    <div style={{ flex: 1, padding: "12px 14px", textAlign: "center", borderRight: last ? "none" : "0.5px solid #eeeeee" }}>
      <div style={{ fontSize: small ? 13 : 18, fontWeight: 600, color: valueColor || "var(--navy)", lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 10, color: "#888", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    </div>
  );
}

function LeadRow({ lead, selected, onToggle, onOpen, onCall, role }) {
  const isReadOnly = role === "read_only";
  const score = getLeadScore(lead);
  const rating = lead.rating || tempLabel(score);
  const rp = ratingPill(rating);
  const name = lead.clinic || lead.title || lead.name || "Unnamed lead";
  // Compute product fit for the row
  let sc = null; try { sc = scoreProducts(lead); } catch {}
  const vbucket = vendorBucket(lead.ehr?.vendor);
  const topDrug = (lead.top_drugs?.[0]?.name || lead.top_drugs?.[0] || null);
  const benes = lead.medicare?.beneficiaries;
  const specialty = lead.specialty || lead.nppes?.specialty_group || "—";
  const role_ = lead.contact_role || lead.role || (specialty.toLowerCase().includes("cardio") ? "Cardiologist" : specialty.toLowerCase().includes("pulmon") ? "Pulmonologist" : "Provider");
  const subParts = isReadOnly
    ? [specialty, `stage: ${lead.stage || "—"}`]
    : [
        role_,
        specialty,
        benes ? `${benes.toLocaleString()} Medicare benes` : null,
        topDrug || null,
        lead.ehr?.vendor ? `${lead.ehr.vendor} (${vbucket.label})` : "No EHR (greenfield)",
      ].filter(Boolean);
  const sub = subParts.join(" · ");
  return (
    <div
      data-testid={`seg-lead-${lead.id || lead.npi}`}
      style={{ background: "#fff", border: "0.5px solid #eeeeee", borderRadius: 10, padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", transition: "border-color 120ms ease, transform 120ms ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#cccccc"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#eeeeee"; }}
    >
      <input type="checkbox" checked={selected} onChange={onToggle} style={{ width: 13, height: 13, flexShrink: 0, cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
      <div style={{ width: 34, height: 34, borderRadius: 99, background: rp.bg, color: rp.c, fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials(name)}</div>
      <div style={{ flex: 1, minWidth: 0 }} onClick={onOpen}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
          <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: rp.bg, color: rp.c, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{rating}</span>
        </div>
        <div title={vbucket.talking_point} style={{ fontSize: 11, color: "#888", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
      </div>
      {!isReadOnly && sc?.primary && (
        <div style={{ flexShrink: 0, textAlign: "right", minWidth: 130 }}>
          <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>Primary</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: primaryDot(sc.primary.product), marginTop: 1 }}>
            {sc.primary.product} <span style={{ fontSize: 11, color: "#888", fontWeight: 500 }}>· AI {sc.primary.score}</span>
          </div>
          <div style={{ height: 3, background: "#f0f0f0", borderRadius: 2, marginTop: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${sc.primary.score}%`, background: primaryDot(sc.primary.product) }} />
          </div>
          {sc.secondary && <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>Secondary: <span style={{ color: "#222" }}>{sc.secondary.product}</span></div>}
        </div>
      )}
      {!isReadOnly && (
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <button onClick={onOpen} style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 6, padding: "3px 9px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", color: "#222" }}>Open</button>
          <button onClick={onCall} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "3px 9px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Call</button>
        </div>
      )}
    </div>
  );
}

function EmptyWorkspace({ onNew }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40, textAlign: "center", background: "linear-gradient(180deg, #fff 0%, #F5F9FF 100%)" }}>
      <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#E6F1FB", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
        <FilterIcon style={{ width: 28, height: 28, color: "#2563eb" }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>Choose a segment</div>
      <div style={{ fontSize: 13, color: "#888", margin: "8px 0 16px", maxWidth: 420 }}>Select a segment from the left, or create a new one to group and work your leads.</div>
      <button onClick={onNew} style={btnStyle.primary}><Plus style={{ width: 13, height: 13 }} /> New segment</button>
    </div>
  );
}

function NoLeadsEmpty({ onEdit }) {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 13, color: "#888" }}>No leads match these filters.</div>
      <div style={{ fontSize: 12, marginTop: 6 }}>
        <button onClick={onEdit} style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
          Try adjusting the filter conditions →
        </button>
      </div>
    </div>
  );
}

/* ── Segment Builder slide-in ───────────────────────────────── */

/* ── ExtendedMultiCheckFilters ───────────────────────────────────────────
 * Iter 139.3 (2026-05-26): full Lead-Focus field universe rendered as
 * collapsible multi-check pill groups, mirroring the Lead Focus →
 * "Information" tab sections. Each value toggle synchronises a single
 * condition row in the parent `conditions` array (one condition per
 * field). Empty selections auto-remove the condition so the rep never
 * has an orphan "0 values" filter dragging the audience to 0.
 */
function ExtendedMultiCheckFilters({ conditions, setConditions, onUserEdit }) {
  const [openGroup, setOpenGroup] = useState(null); // string id of expanded group
  const [search, setSearch] = useState("");
  const selectedValuesFor = (key) => {
    const c = conditions.find((x) => x.field === key);
    return Array.isArray(c?.value) ? c.value : [];
  };
  const toggleValue = (key, opt) => {
    onUserEdit?.();
    setConditions((arr) => {
      const idx = arr.findIndex((x) => x.field === key);
      const cur = idx >= 0 && Array.isArray(arr[idx].value) ? arr[idx].value : [];
      const next = cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt];
      if (!next.length) {
        // Auto-remove empty condition rows.
        return idx >= 0 ? arr.filter((_, i) => i !== idx) : arr;
      }
      if (idx >= 0) {
        const copy = arr.slice();
        copy[idx] = { ...copy[idx], value: next };
        return copy;
      }
      return [...arr, { field: key, value: next }];
    });
  };
  const clearField = (key) => {
    onUserEdit?.();
    setConditions((arr) => arr.filter((x) => x.field !== key));
  };
  const totalSelectedInGroup = (g) => g.fields.reduce((n, f) => n + selectedValuesFor(f.key).length, 0);

  const q = search.trim().toLowerCase();
  const filteredGroups = !q
    ? EXTENDED_FILTER_GROUPS
    : EXTENDED_FILTER_GROUPS
        .map((g) => ({
          ...g,
          fields: g.fields.filter(
            (f) => f.label.toLowerCase().includes(q) || (f.options || []).some((o) => String(o).toLowerCase().includes(q))
          ),
        }))
        .filter((g) => g.fields.length);

  return (
    <div data-testid="seg-builder-extended-filters" style={{ marginTop: 18, borderTop: "0.5px solid #eee", paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: "0.06em", fontWeight: 600 }}>All Lead-Focus fields · multi-check</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
            Every field on the Lead Focus → Information tab. Click any value to add it to the filter — empty selections auto-remove.
          </div>
        </div>
      </div>

      <input
        data-testid="seg-builder-extended-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Find a field or value… (e.g. 'budget', 'pulmonology', 'no show')"
        style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "inherit", outline: "none", marginBottom: 10 }}
        onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
      />

      {filteredGroups.length === 0 && (
        <div style={{ fontSize: 12, color: "#888", padding: "10px 0" }}>No fields or values match "{search}".</div>
      )}

      {filteredGroups.map((g) => {
        const groupSelected = totalSelectedInGroup(g);
        // When searching, auto-expand every group with hits. Otherwise
        // use the click-toggled openGroup id.
        const isOpen = q ? true : openGroup === g.id;
        return (
          <div key={g.id} data-testid={`seg-ext-group-${g.id}`} style={{ borderBottom: "0.5px solid #eee" }}>
            <button
              onClick={() => setOpenGroup((cur) => (cur === g.id ? null : g.id))}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 0", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
            >
              <ChevronDown style={{ width: 12, height: 12, color: "#888", transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.12s" }} />
              <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>{g.label}</div>
              {groupSelected > 0 && (
                <span style={{ background: "#EEEDFE", color: "#3C3489", borderRadius: 999, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>{groupSelected}</span>
              )}
            </button>
            {isOpen && (
              <div style={{ padding: "0 0 12px 20px" }}>
                {g.fields.map((f) => {
                  const selected = selectedValuesFor(f.key);
                  const visibleOptions = q
                    ? f.options.filter((o) => String(o).toLowerCase().includes(q) || f.label.toLowerCase().includes(q))
                    : f.options;
                  return (
                    <div key={f.key} data-testid={`seg-ext-field-${f.key}`} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--navy)", flex: 1 }}>{f.label}</div>
                        <span style={{ fontSize: 10, color: "#888" }}>{f.multi ? "Lead has any" : "Lead matches"}</span>
                        {selected.length > 0 && (
                          <button
                            data-testid={`seg-ext-clear-${f.key}`}
                            onClick={() => clearField(f.key)}
                            style={{ background: "transparent", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 10, fontFamily: "inherit", padding: 0, marginLeft: 6 }}
                          >
                            Clear ({selected.length})
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {visibleOptions.map((opt) => {
                          const on = selected.includes(opt);
                          return (
                            <button
                              key={opt}
                              data-testid={`seg-ext-opt-${f.key}-${String(opt).replace(/\s+/g, "_")}`}
                              onClick={() => toggleValue(f.key, opt)}
                              style={{
                                fontSize: 11, padding: "3px 10px", borderRadius: 999,
                                background: on ? "#3C3489" : "#fff",
                                color: on ? "#fff" : "var(--navy)",
                                border: `0.5px solid ${on ? "#3C3489" : "#ddd"}`,
                                cursor: "pointer", fontFamily: "inherit",
                                transition: "background 0.08s, color 0.08s, border-color 0.08s",
                              }}
                            >
                              {on ? "✓ " : ""}{opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function SegmentBuilder({ initial, dataset, onClose, onSave, onDelete, canCreateTeamSeg }) {
  const editing = !!initial;
  const confirm = useConfirm();
  const [mode, setMode] = useState("ai"); // "ai" | "manual"  — Section 1 spec
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiThinking, setAiThinking] = useState(false);
  const [name, setName] = useState(initial?.name || "");
  const [type, setType] = useState(initial?.type || "live");
  const [op, setOp] = useState(initial?.op || "AND");
  const [conditions, setConditions] = useState(initial?.conditions || []);
  const [shareTeam, setShareTeam] = useState(!!initial?.shareTeam);
  const [search, setSearch] = useState("");
  const [aiInsight, setAiInsight] = useState(null);
  // ── Iter 139 (user bug 2026-05-25): Lumina pinned audience.
  //   When the AI-assisted brief runs through `/api/dialer/ai-brief-parse`
  //   Lumina returns a list of `matched_ids` that includes leads found
  //   via unstructured fulltext fallback (e.g. "COPD" hiding inside
  //   `ai_score.primary.campaigns.name`). The local FILTER_FIELDS
  //   schema can't represent those signals, so a pure structured
  //   evaluation returns 0 — exactly what the user reported. We hold
  //   the matched IDs in `luminaPinIds` and use them as the audience
  //   when present. Cleared whenever the user manually edits conditions
  //   so the local evaluator takes over again.
  const [luminaPinIds, setLuminaPinIds] = useState(null); // null = no pin, []/[...ids] = pinned
  const [luminaMeta, setLuminaMeta] = useState(null);     // { fallback_used, fallback_keywords, evidence, ... }

  const filteredFields = useMemo(() => FILTER_FIELDS.filter((f) => f.label.toLowerCase().includes(search.toLowerCase())), [search]);
  const matches = useMemo(() => {
    // When Lumina pinned a frozen audience, that wins — even when the
    // local structured evaluation would zero out. This is what makes
    // "pulmonologists with COPD …" return real leads instead of 0.
    if (Array.isArray(luminaPinIds)) {
      const pin = new Set(luminaPinIds);
      return dataset.filter((l) => pin.has(l.id || l.npi));
    }
    return dataset.filter((l) => evalSegment(l, conditions, op));
  }, [dataset, conditions, op, luminaPinIds]);

  // Generate a lightweight AI insight locally (deterministic)
  useEffect(() => {
    if (!conditions.length) return setAiInsight(null);
    const stateCounts = {};
    matches.forEach((l) => { const s = l.state || l.practice?.state; if (s) stateCounts[s] = (stateCounts[s] || 0) + 1; });
    const topStates = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]).slice(0, 2);
    const avgScore = matches.length ? Math.round(matches.reduce((s, l) => s + (getLeadScore(l)), 0) / matches.length) : 0;
    let line;
    if (topStates.length && topStates[0][1] / Math.max(matches.length, 1) >= 0.4) {
      line = `${Math.round((topStates[0][1] / matches.length) * 100)}% of these leads are in ${topStates.map((s) => s[0]).join(" and ")}. Consider assigning to your ET-timezone agents.`;
    } else if (avgScore >= 70) {
      line = `These leads have AI scores averaging ${avgScore} — strong conversion candidates for a demo-focused campaign.`;
    } else if (avgScore <= 40) {
      line = `Average AI score is ${avgScore}. An SMS-first re-engagement may convert better than cold calls.`;
    } else {
      line = `${matches.length} matching leads · avg AI score ${avgScore}. Mid-funnel mix — pair value pitch with social proof.`;
    }
    setAiInsight(line);
  }, [conditions, matches]);

  const addField = (f) => {
    if (conditions.find((c) => c.field === f.id)) return;
    const cond = f.type === "range" ? { field: f.id, min: 0, max: f.id === "ai_score" ? 100 : 999 } : { field: f.id, value: [] };
    setConditions((arr) => [...arr, cond]);
    setSearch("");
    setLuminaPinIds(null); // manual edit → release the Lumina audience lock
  };
  const removeCond = (i) => { setConditions((arr) => arr.filter((_, idx) => idx !== i)); setLuminaPinIds(null); };
  const updateCond = (i, patch) => { setConditions((arr) => arr.map((c, idx) => idx === i ? { ...c, ...patch } : c)); setLuminaPinIds(null); };

  const handleSave = () => {
    if (!name.trim()) { toast.error("Give your segment a name"); return; }
    onSave({
      id: initial?.id,
      name: name.trim(),
      type, op, conditions, shareTeam,
      // Iter 139: when Lumina pinned the audience, persist the frozen
      // ID list so the saved segment shows the right count without
      // having to re-evaluate against the (incomplete) structured filters.
      luminaPinIds: Array.isArray(luminaPinIds) ? luminaPinIds : null,
      luminaMeta: luminaMeta || null,
    });
  };

  // Section 1 — translate plain-English prompt → structured filter rows
  // Iter 139 (user bug 2026-05-25): Lumina is now the primary NL→filters
  // engine here too (matches the Sales Dialer campaign builder). The
  // legacy `/api/ai/segment-parse` kept hallucinating + couldn't surface
  // unstructured fulltext matches like "COPD". On Lumina success we
  // ALSO capture `lumina.matched_ids` so the audience preview shows the
  // real Lumina-matched set even when the structured filters can't
  // represent the soft signals. Falls back gracefully to the legacy
  // endpoint and finally to a local heuristic.
  const handleAIGenerate = async () => {
    const t = aiPrompt.toLowerCase().trim();
    if (!t) { toast.error("Describe the leads you want first"); return; }
    setAiThinking(true);

    // ── Heuristic builder (kept as last-resort fallback) ──
    const buildHeuristic = () => {
      const out = [];
      const specHits = (FILTER_FIELDS.find((f) => f.id === "specialty")?.options || []).filter((o) => t.includes(o.toLowerCase()));
      if (/cardio/.test(t) && !specHits.includes("Cardiology")) specHits.push("Cardiology");
      if (/pulmon/.test(t) && !specHits.includes("Pulmonology")) specHits.push("Pulmonology");
      if (/family/.test(t) && !specHits.includes("Family Medicine")) specHits.push("Family Medicine");
      if (/internal/.test(t) && !specHits.includes("Internal Medicine")) specHits.push("Internal Medicine");
      if (/geriatric/.test(t) && !specHits.includes("Geriatric Medicine")) specHits.push("Geriatric Medicine");
      if (specHits.length) out.push({ field: "specialty", value: specHits });
      const stateHits = (FILTER_FIELDS.find((f) => f.id === "state")?.options || []).filter((s) => new RegExp(`\\b${s}\\b`, "i").test(aiPrompt));
      if (stateHits.length) out.push({ field: "state", value: stateHits });
      if (/northeast|north east/.test(t)) out.push({ field: "region", value: ["NE"] });
      else if (/southeast|south east/.test(t)) out.push({ field: "region", value: ["SE"] });
      else if (/midwest|mid west/.test(t)) out.push({ field: "region", value: ["MW"] });
      else if (/southwest|south west/.test(t)) out.push({ field: "region", value: ["SW"] });
      if (/\bhot\b/.test(t)) out.push({ field: "rating", value: ["Hot"] });
      else if (/\bwarm\b/.test(t)) out.push({ field: "rating", value: ["Warm"] });
      const scoreMatch = t.match(/(?:ai score|score)\s*(?:>=|≥|>|over|above|of)?\s*(\d{2,3})/);
      if (scoreMatch) out.push({ field: "ai_score", min: Number(scoreMatch[1]), max: 100 });
      else if (/strong fit|high score|hot leads/.test(t)) out.push({ field: "ai_score", min: 70, max: 100 });
      const beneMatch = t.match(/(\d{2,4})\+?\s*(?:medicare\s+)?(?:patients|benes|beneficiaries)/);
      if (beneMatch) out.push({ field: "medicare_benes", min: Number(beneMatch[1]), max: 99999 });
      if (/legacy|stranded|practice fusion|aprima|sunset/.test(t)) out.push({ field: "ehr_vendor", value: ["Practice Fusion"] });
      else if (/eclinical|ecw/.test(t)) out.push({ field: "ehr_vendor", value: ["eClinicalWorks"] });
      else if (/epic/.test(t)) out.push({ field: "ehr_vendor", value: ["Epic"] });
      const stale = t.match(/(\d+)\+?\s*(?:days?|d)\s*(?:no contact|stale|since)/);
      if (stale) out.push({ field: "last_contact_days", min: Number(stale[1]), max: 999 });
      else if (/never contacted/.test(t)) out.push({ field: "last_contact_days", min: 9999, max: 99999 });
      const products = ["EHR","PM","RCM","APCM","CCM","RPM","AWV"].filter((p) => new RegExp(`\\b${p}\\b`).test(aiPrompt.toUpperCase()));
      if (/copd|inhaler/.test(t)) products.push("RPM");
      if (products.length) out.push({ field: "product_fit", value: [...new Set(products)] });
      return out;
    };

    // ── Map Lumina dialer-shaped conditions → LeadSegments fields ──
    // Iter 188.4 (Feb 2026) — hardened mapping. Previously any
    // Lumina-emitted field id we didn't recognise (`source`,
    // `never_contacted`) was SILENTLY dropped by the `recognised`
    // filter, and multi-value fields with case-mismatched or empty
    // entries (e.g. specialty=['', 'internal medicine']) selected
    // nothing in the UI. Result: the drawer showed 3 of 5 criteria
    // and the lead count flipped 263 → 477 the moment the Lumina
    // pin was released. Fix: explicit alias table, case-insensitive
    // option matching, empty-value stripping, and a console warning
    // on any dropped conditions.
    const recognised = new Set((FILTER_FIELDS || []).map((f) => f.id));
    const LUMINA_FIELD_ALIASES = {
      source: "lead_source",
      never_contacted: "last_contact_days",
    };
    const mapLuminaConditions = (rawConds) => {
      const out = [];
      const dropped = [];
      for (const c of rawConds || []) {
        if (!c || !c.field) continue;
        const fieldId = LUMINA_FIELD_ALIASES[c.field] || c.field;
        if (!recognised.has(fieldId)) { dropped.push(c.field); continue; }
        const conf = { field: fieldId };
        // `never_contacted` → sentinel range that matches leads with
        // no `last_contact_date`; see `_rawEvalCondition.last_contact_days`.
        if (c.field === "never_contacted") {
          conf.min = 9999;
          conf.max = 99999;
          out.push(conf);
          continue;
        }
        if (Array.isArray(c.value) && c.value.length) {
          const raw = c.value.map((x) => String(x ?? "").trim()).filter(Boolean);
          const fieldDef = FILTER_FIELDS.find((f) => f.id === fieldId);
          let normalized;
          if (fieldId === "state") {
            // Always uppercase 2-letter codes; keep only known 50+DC.
            const known = new Set(fieldDef?.options || []);
            normalized = raw.map((x) => x.toUpperCase()).filter((x) => known.has(x));
          } else if (Array.isArray(fieldDef?.options) && fieldDef.options.length) {
            // Case-insensitive lookup into the canonical option list;
            // fall back to the trimmed raw value if no option matches.
            normalized = raw
              .map((v) => fieldDef.options.find((o) => String(o).toLowerCase() === v.toLowerCase()) || null)
              .filter(Boolean);
          } else {
            normalized = raw;
          }
          const deduped = [...new Set(normalized)];
          if (deduped.length) conf.value = deduped;
        }
        if (typeof c.min === "number") conf.min = c.min;
        if (typeof c.max === "number") conf.max = c.max;
        // Only emit conditions that actually carry a filter (value or range).
        if (conf.value || conf.min != null || conf.max != null) out.push(conf);
      }
      if (dropped.length) {
        console.warn("[LeadSegments] mapLuminaConditions dropped unknown fields:", dropped);
      }
      return out;
    };

    let next = [];
    let suggestedName = "";
    let viaLumina = false;
    let pinIds = null;
    let lumina = null;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    const { default: axios } = await import("axios");

    // ── 1) Primary: Lumina via /api/dialer/ai-brief-parse ──
    try {
      const res = await axios.post(
        `${apiBase}/dialer/ai-brief-parse`,
        { prompt: aiPrompt },
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {}, timeout: 22000 }
      );
      const d = res.data || {};
      lumina = d.lumina || null;
      if (d.source === "lumina") {
        next = mapLuminaConditions(d.conditions || []);
        suggestedName = d.suggested_name || "";
        viaLumina = true;
        // Capture matched IDs (includes fulltext-fallback hits like COPD)
        if (Array.isArray(lumina?.matched_ids) && lumina.matched_ids.length) {
          pinIds = lumina.matched_ids;
        }
      }
    } catch (_) { /* fall through to legacy */ }

    // ── 2) Legacy /api/ai/segment-parse if Lumina returned nothing ──
    if (!next.length && !pinIds) {
      try {
        const res = await axios.post(
          `${apiBase}/ai/segment-parse`,
          { prompt: aiPrompt },
          { headers: tk ? { Authorization: `Bearer ${tk}` } : {}, timeout: 18000 }
        );
        const d = res.data || {};
        // Iter 188.4 — reuse mapLuminaConditions (alias + normalize) so
        // segment-parse's `lead_source` / lowercase specialty / never-
        // contacted sentinels round-trip through the UI cleanly.
        next = mapLuminaConditions(d.conditions || []);
        suggestedName = suggestedName || d.suggested_name || "";
      } catch (_) { /* fall through */ }
    }

    // ── 3) Last-resort: local heuristic ──
    if (!next.length && !pinIds) next = buildHeuristic();

    if (!next.length && !pinIds) {
      toast.error("Couldn't extract filters — try mentioning specialty, region, score, or vendor");
      setAiThinking(false);
      return;
    }
    setConditions(next);
    setLuminaPinIds(pinIds); // null when no Lumina hit
    setLuminaMeta(lumina);
    // Suggested name
    if (!name) {
      // Iter 188.4 — sanitize the backend's `suggested_name`, which
      // is sometimes emitted with a dangling separator (e.g. "MS · ")
      // when only one criterion is present. Strip leading/trailing
      // ` · ` and collapse consecutive separators.
      const cleanSuggested = String(suggestedName || "")
        .replace(/^(?:\s*·\s*)+/, "")
        .replace(/(?:\s*·\s*)+$/, "")
        .replace(/(?:\s*·\s*){2,}/g, " · ")
        .trim();
      if (cleanSuggested) {
        setName(cleanSuggested);
      } else {
        const specHit = next.find((c) => c.field === "specialty")?.value?.[0];
        const parts = [];
        if (specHit) parts.push(specHit);
        if (next.find((c) => c.field === "rating")?.value?.includes("Hot")) parts.push("Hot");
        if (next.find((c) => c.field === "ehr_vendor")?.value?.length) parts.push("Legacy EHR");
        if (next.find((c) => c.field === "ai_score")) parts.push("strong fit");
        setName(parts.length ? parts.join(" · ") : "AI segment");
      }
    }
    setMode("manual"); // Show the parsed rows so the user can review
    setAiThinking(false);
    const pinCount = Array.isArray(pinIds) ? pinIds.length : 0;
    if (pinIds && pinCount) {
      const fb = lumina?.fallback_used ? " · fulltext match" : "";
      toast.success(`Lumina pinned ${pinCount} matching lead${pinCount === 1 ? "" : "s"}${fb} — review & save`);
    } else {
      toast.success(`Generated ${next.length} filter ${next.length === 1 ? "row" : "rows"}${viaLumina ? " · Lumina" : ""} — review & save`);
    }
  };

  return (
    <div data-testid="seg-builder" style={{ position: "fixed", inset: 0, zIndex: 9500, display: "flex", justifyContent: "flex-end", background: "rgba(11,17,32,0.20)", backdropFilter: "blur(2px)" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 520, height: "100%", paddingBottom: 64, background: "#fff", borderLeft: "0.5px solid #eee", boxShadow: "-12px 0 32px -8px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", position: "relative" }}>
        {showAllMatches && (
          <SegmentAllMatchesPanel matches={matches} onBack={() => setShowAllMatches(false)} />
        )}
        <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>{editing ? "Edit segment" : "New segment"}</h3>
          <button data-testid="seg-builder-close" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#888", padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
        </div>

        {/* Section 1 — Mode tabs */}
        <div style={{ display: "flex", gap: 4, padding: "10px 18px 0", borderBottom: "0.5px solid #f5f5f5" }}>
          {[
            { id: "ai", label: "✦ AI-assisted" },
            { id: "manual", label: "Manual filters" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`seg-builder-mode-${t.id}`}
              onClick={() => setMode(t.id)}
              style={{ padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", background: "transparent", border: "none", borderBottom: `2px solid ${mode === t.id ? "#2563eb" : "transparent"}`, color: mode === t.id ? "#2563eb" : "#555", marginBottom: -1, fontFamily: "inherit" }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mode === "ai" && (
          <div style={{ padding: "14px 18px 0" }}>
            <div style={{ fontSize: 12, color: "#444", lineHeight: 1.55, marginBottom: 8 }}>
              Describe the leads you want — Claude will translate it into filter rules using the scoring spec.
            </div>
            <textarea
              data-testid="seg-builder-ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. 'Pulmonologists with COPD drug signals and 200+ Medicare patients' or 'Legacy EHR practices in FL and NY' or 'Hot Cardiology with AI score over 75'"
              style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontFamily: "inherit", outline: "none", minHeight: 92, resize: "vertical" }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <div style={{ fontSize: 11, color: "#888" }}>
                {conditions.length > 0 ? `${matches.length} leads match the current rules` : "No filters yet"}
              </div>
              <button data-testid="seg-builder-ai-generate" onClick={handleAIGenerate} disabled={aiThinking || !aiPrompt.trim()} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, cursor: aiThinking || !aiPrompt.trim() ? "not-allowed" : "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6, opacity: aiThinking || !aiPrompt.trim() ? 0.5 : 1 }}>
                {aiThinking ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Sparkles style={{ width: 12, height: 12 }} />}
                Generate segment →
              </button>
            </div>
            {conditions.length > 0 && (
              <div style={{ marginTop: 10, padding: "8px 10px", background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 8, fontSize: 11, color: "#3C3489" }}>
                ✦ Switched to <strong>Manual filters</strong> tab — review the {conditions.length} extracted rule{conditions.length === 1 ? "" : "s"}, then Save.
              </div>
            )}
            {Array.isArray(luminaPinIds) && luminaPinIds.length > 0 && (
              <div data-testid="seg-builder-lumina-pin" style={{ marginTop: 8, padding: "8px 10px", background: "#E1F5EE", border: "0.5px solid #9FE1CB", borderRadius: 8, fontSize: 11, color: "#0F6E56" }}>
                ✦ Lumina pinned <strong>{luminaPinIds.length}</strong> matching lead{luminaPinIds.length === 1 ? "" : "s"}
                {luminaMeta?.fallback_used ? " (fulltext match)" : ""} — saving will freeze this audience. Edit a filter to release the pin and re-evaluate.
              </div>
            )}
          </div>
        )}

        <div style={{ padding: "14px 18px 0", display: mode === "manual" ? "block" : "none" }}>
          {Array.isArray(luminaPinIds) && luminaPinIds.length > 0 && (
            <div data-testid="seg-builder-lumina-pin-manual" style={{ marginBottom: 10, padding: "8px 10px", background: "#E1F5EE", border: "0.5px solid #9FE1CB", borderRadius: 8, fontSize: 11, color: "#0F6E56", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div>
                ✦ <strong>Lumina pinned {luminaPinIds.length}</strong> matching lead{luminaPinIds.length === 1 ? "" : "s"}
                {luminaMeta?.fallback_used ? ` via fulltext on "${(luminaMeta.fallback_keywords || []).join(", ")}"` : ""} — edits below release the pin.
              </div>
              <button
                data-testid="seg-builder-lumina-pin-release"
                onClick={() => { setLuminaPinIds(null); setLuminaMeta(null); }}
                style={{ background: "transparent", border: "none", color: "#0F6E56", fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", whiteSpace: "nowrap" }}
              >
                Release pin
              </button>
            </div>
          )}
          <input
            data-testid="seg-builder-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this segment... e.g. Northeast Cardiology Hot Leads."
            style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "inherit", outline: "none" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
          />
        </div>

        <div style={{ padding: "12px 18px 4px", display: mode === "manual" ? "flex" : "none", gap: 8 }}>
          {[
            { id: "live",   label: "Live · auto-updates" },
            { id: "static", label: "Static · fixed list" },
          ].map((o) => (
            <button
              key={o.id}
              data-testid={`seg-type-${o.id}`}
              onClick={() => setType(o.id)}
              style={{ flex: 1, border: "0.5px solid", borderColor: type === o.id ? "#2563eb" : "#ddd", background: type === o.id ? "#E6F1FB" : "#fff", color: type === o.id ? "#185FA5" : "#222", borderRadius: 999, padding: "6px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "14px 18px 6px", fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: "0.06em", fontWeight: 600, display: mode === "manual" ? "block" : "none" }}>Filter conditions</div>

        <div style={{ padding: "0 18px 8px", position: "relative", display: mode === "manual" ? "block" : "none" }}>
          <input
            data-testid="seg-builder-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filter fields... (e.g. specialty, AI score, region, budget)"
            style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px", fontSize: 12, fontFamily: "inherit", outline: "none" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
          />
          {search && (
            <div style={{ position: "absolute", left: 18, right: 18, top: "100%", marginTop: 4, background: "#fff", border: "0.5px solid #eee", borderRadius: 8, boxShadow: "0 8px 24px -8px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto", zIndex: 10 }}>
              {filteredFields.length === 0 ? (
                <div style={{ padding: 10, fontSize: 11, color: "#888" }}>No fields match.</div>
              ) : filteredFields.map((f) => (
                <button key={f.id} onClick={() => addField(f)} data-testid={`seg-field-${f.id}`} style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontFamily: "inherit", color: "#222" }} onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  + {f.label} <span style={{ color: "#888", fontSize: 10 }}>· {f.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "0 18px", flex: 1, overflowY: "auto" }}>
          {conditions.length === 0 ? (
            <div style={{ padding: "10px 0", fontSize: 12, color: "#888" }}>No filter conditions yet — search above to add fields.</div>
          ) : conditions.map((c, i) => {
            const f = FILTER_FIELDS.find((x) => x.id === c.field);
            const ext = EXTENDED_FILTER_FIELD_MAP[c.field];
            return (
              <div key={i}>
                {i > 0 && (
                  <div style={{ display: "flex", justifyContent: "center", padding: "6px 0" }}>
                    <button onClick={() => setOp(op === "AND" ? "OR" : "AND")} style={{ background: op === "AND" ? "#E6F1FB" : "#EEEDFE", color: op === "AND" ? "#185FA5" : "#3C3489", border: "0.5px solid", borderColor: op === "AND" ? "#B5D4F4" : "#CECBF6", borderRadius: 999, padding: "2px 12px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{op}</button>
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "0.5px solid #eee" }}>
                  {/* Iter 173 (Danny — segment include/exclude):
                      Per-row Include/Exclude toggle. Pre-selected:
                      Include. Click to flip the row to Exclude — the
                      eval layer will negate the underlying match.
                      Empty-value rows ignore the flag (footgun guard
                      in evalCondition) so toggling Exclude on an
                      unset filter doesn't suddenly hide every lead. */}
                  <button
                    onClick={() => updateCond(i, { negate: !c.negate })}
                    data-testid={`seg-cond-${i}-mode-toggle`}
                    data-mode={c.negate ? "exclude" : "include"}
                    title={c.negate ? "This row EXCLUDES matching leads — click to switch to Include" : "This row INCLUDES matching leads — click to switch to Exclude"}
                    style={{
                      background: c.negate ? "#FEE2E2" : "#DCFCE7",
                      color: c.negate ? "#991B1B" : "#14532D",
                      border: `0.5px solid ${c.negate ? "#FCA5A5" : "#86EFAC"}`,
                      borderRadius: 999,
                      padding: "2px 10px",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      letterSpacing: 0.3,
                      textTransform: "uppercase",
                      minWidth: 64,
                    }}
                  >
                    {c.negate ? "Exclude" : "Include"}
                  </button>
                  <div style={{ minWidth: 130, fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>{f?.label || ext?.label || c.field}</div>
                  {f?.type === "multi" && (
                    <select multiple value={c.value || []} onChange={(e) => updateCond(i, { value: Array.from(e.target.selectedOptions, (o) => o.value) })} style={{ flex: 1, border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 6px", fontSize: 11, fontFamily: "inherit", minHeight: 26, maxHeight: 70 }}>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {f?.type === "range" && (
                    <>
                      <input type="number" value={c.min ?? 0} onChange={(e) => updateCond(i, { min: Number(e.target.value) })} placeholder="min" style={{ width: 70, border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 6px", fontSize: 11, fontFamily: "inherit" }} />
                      <span style={{ fontSize: 10, color: "#888" }}>to</span>
                      <input type="number" value={c.max ?? 999} onChange={(e) => updateCond(i, { max: Number(e.target.value) })} placeholder="max" style={{ width: 70, border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 6px", fontSize: 11, fontFamily: "inherit" }} />
                    </>
                  )}
                  {/* Iter 140.8 (Peter 49b1eece) — tag input for city /
                       NPPES taxonomy. Comma- or Enter-separated. */}
                  {f?.type === "tag" && (
                    <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 6px", minHeight: 26 }}>
                      {(c.value || []).map((tag, ti) => (
                        <span key={`${tag}-${ti}`} style={{ background: TONE_PILL[f.tone]?.bg || "#E1F5EE", color: TONE_PILL[f.tone]?.color || "#0F6E56", border: "0.5px solid", borderColor: TONE_PILL[f.tone]?.color || "#0F6E56", borderRadius: 999, padding: "1px 8px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {tag}
                          <button onClick={() => updateCond(i, { value: (c.value || []).filter((_, x) => x !== ti) })} style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 11 }}>×</button>
                        </span>
                      ))}
                      <input
                        type="text"
                        placeholder={(c.value || []).length ? "" : (f.placeholder || "Type & press Enter")}
                        onKeyDown={(e) => {
                          const t = e.target.value.trim();
                          if ((e.key === "Enter" || e.key === ",") && t) {
                            e.preventDefault();
                            if (!(c.value || []).includes(t)) {
                              updateCond(i, { value: [...(c.value || []), t] });
                            }
                            e.target.value = "";
                          } else if (e.key === "Backspace" && !e.target.value && (c.value || []).length) {
                            updateCond(i, { value: (c.value || []).slice(0, -1) });
                          }
                        }}
                        style={{ flex: 1, minWidth: 80, border: "none", outline: "none", fontSize: 11, padding: "2px 0", background: "transparent", fontFamily: "inherit" }}
                      />
                    </div>
                  )}
                  {/* Extended Lead-Focus fields rendered as a pill summary
                       — the canonical edit surface is the multi-check
                       section below, but reps can quickly see + remove a
                       condition here. */}
                  {!f && ext && (
                    <div style={{ flex: 1, fontSize: 11, color: "#3C3489", display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {(c.value || []).map((v) => (
                        <span key={v} style={{ background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 999, padding: "2px 8px" }}>{v}</span>
                      ))}
                    </div>
                  )}
                  <button onClick={() => removeCond(i)} style={{ background: "transparent", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 12, fontFamily: "inherit", marginLeft: "auto" }}>× Remove</button>
                </div>
              </div>
            );
          })}

          {/* iter 139.3 (2026-05-26 user request): "All Lead-Focus fields"
              multi-check section. Mirrors every option-based field on
              the Lead Focus → Information tab so reps can build a
              segment off any value they enter into the lead profile,
              not just the ~10 hand-picked structured filters above. */}
          <ExtendedMultiCheckFilters
            conditions={conditions}
            setConditions={setConditions}
            onUserEdit={() => { setLuminaPinIds(null); }}
          />
        </div>

        {/* Live preview */}
        <div style={{ borderTop: "0.5px solid #eee", padding: "12px 18px", background: "#FAFCFE" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: "0.06em", fontWeight: 600 }}>Matching leads</div>
            <div data-testid="seg-builder-count" style={{ fontSize: 12, color: "#185FA5", fontWeight: 600 }}>{matches.length} leads match</div>
          </div>
          {matches.length === 0 ? (
            <div style={{ fontSize: 12, color: "#888", textAlign: "center", padding: 14 }}>No leads match these filters.</div>
          ) : matches.slice(0, 5).map((l) => (
            <div key={l.id || l.npi} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "0.5px solid #eee" }}>
              <div style={{ width: 28, height: 28, borderRadius: 99, background: "#E6F1FB", color: "#185FA5", fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{initials(l.clinic || l.title || l.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.clinic || l.title || l.name}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{l.specialty || l.nppes?.specialty_group || "—"}</div>
              </div>
              <div style={{ fontSize: 11, color: "#185FA5", fontWeight: 600 }}>AI {getLeadScore(l)}</div>
            </div>
          ))}
          {matches.length > 5 && (
            <button
              type="button"
              data-testid="seg-view-all-matches"
              onClick={() => setShowAllMatches(true)}
              style={{ width: "100%", textAlign: "right", fontSize: 11, marginTop: 6, background: "transparent", border: 0, cursor: "pointer", padding: 0, color: "#2563eb", fontWeight: 600, fontFamily: "inherit" }}
            >
              View all {matches.length} in segment →
            </button>
          )}
          {aiInsight && (
            <div style={{ marginTop: 10, background: "#EEEDFE", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#3C3489", display: "flex", gap: 6 }}>
              <Sparkles style={{ width: 12, height: 12, flexShrink: 0, marginTop: 2 }} /><span>{aiInsight}</span>
            </div>
          )}
        </div>

        <div style={{ borderTop: "0.5px solid #eee", padding: "12px 18px", display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          {editing && (
            <button data-testid="seg-builder-delete" onClick={async () => {
              const ok = await confirm({
                title: "Delete segment?",
                body: `Delete segment "${initial.name}"? This cannot be undone.`,
                confirmLabel: "Delete segment",
                confirmTone: "danger",
                testid: "seg-builder-delete-confirm",
              });
              if (ok) onDelete(initial.id);
            }} style={{ background: "transparent", border: "none", color: "#A32D2D", fontSize: 12, cursor: "pointer", fontFamily: "inherit", marginRight: "auto" }}><Trash2 style={{ width: 12, height: 12, display: "inline-block", verticalAlign: "middle" }} /> Delete segment</button>
          )}
          {canCreateTeamSeg && (
            <label style={{ fontSize: 11, color: "#555", display: "inline-flex", alignItems: "center", gap: 6, marginRight: "auto", cursor: "pointer" }}>
              <input type="checkbox" checked={shareTeam} onChange={(e) => setShareTeam(e.target.checked)} style={{ cursor: "pointer" }} /> Share with team
            </label>
          )}
          <button onClick={onClose} style={btnStyle.outline}>Cancel</button>
          <button data-testid="seg-builder-save" onClick={handleSave} style={btnStyle.primary}><Check style={{ width: 13, height: 13 }} /> Save segment</button>
        </div>
      </div>
    </div>
  );
}

/* ── Mass Email modal ───────────────────────────────────────── */
function MassEmailModal({ segment, recipients, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const sendable = recipients.filter((l) => l.email || l.contact_email).length;
  const skipped = recipients.length - sendable;
  return (
    <ModalShell title={`Mass email — ${segment?.name || `${recipients.length} selected leads`}`} onClose={onClose} width={620}>
      <div style={{ padding: "14px 20px" }}>
        <div style={{ background: "#E6F1FB", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#185FA5", marginBottom: 6 }}>
          {sendable} leads will receive this email
        </div>
        {skipped > 0 && <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>Leads with no email address ({skipped}) will be skipped.</div>}

        <input data-testid="seg-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject..." style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontFamily: "inherit", marginBottom: 10, outline: "none" }} />
        <textarea data-testid="seg-email-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your email…" style={{ width: "100%", minHeight: 160, border: "0.5px solid #ddd", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <button
            data-testid="seg-email-ai-draft"
            onClick={() => {
              setDrafting(true);
              setTimeout(() => {
                setSubject((s) => s || `Re-engagement note for ${segment?.name || "your team"}`);
                setBody(`Hi {{first_name}},\n\nI'm reaching out because your practice fits the profile of clinics our team is helping right now — ${segmentSummary(segment)}.\n\nWould a 15-minute revenue-math call this week be useful? I'll bring numbers specific to {{practice_name}}.\n\nThanks,\n{{rep_name}}`);
                setDrafting(false);
                toast.success("AI draft inserted — edit and send");
              }, 700);
            }}
            style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 8, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Sparkles style={{ width: 12, height: 12 }} /> {drafting ? "Drafting…" : "Draft with AI"}
          </button>
          {["{{first_name}}", "{{practice_name}}", "{{specialty}}"].map((m) => (
            <button key={m} onClick={() => setBody((b) => b + " " + m)} style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>{m}</button>
          ))}
        </div>
      </div>
      <ModalFooter>
        <button onClick={onClose} style={btnStyle.outline}>Cancel</button>
        <button style={btnStyle.outline}><CalendarIcon style={{ width: 12, height: 12 }} /> Schedule send</button>
        <button
          data-testid="seg-email-send"
          onClick={() => { toast.success(`Mass email queued · ${sendable} recipients`); onClose(); }}
          style={btnStyle.primary}
        >
          <Send style={{ width: 12, height: 12 }} /> Send to {sendable} leads
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

/* ── Mass SMS modal ─────────────────────────────────────────── */
function MassSmsModal({ segment, recipients, onClose }) {
  const [body, setBody] = useState("");
  const sendable = recipients.filter((l) => l.phone || l.contact_phone || l.practice?.phone).length;
  const skipped = recipients.length - sendable;
  const len = body.length;
  const counterColor = len >= 160 ? "#A32D2D" : len >= 140 ? "#BA7517" : "#888";
  return (
    <ModalShell title={`Mass SMS — ${segment?.name || `${recipients.length} selected leads`}`} onClose={onClose} width={480}>
      <div style={{ padding: "14px 20px" }}>
        <div style={{ background: "#E1F5EE", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#0F6E56", marginBottom: 6 }}>
          {sendable} leads will receive this SMS
        </div>
        {skipped > 0 && <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>Leads with no phone ({skipped}) will be skipped.</div>}
        <textarea data-testid="seg-sms-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your SMS… (160 char per segment)" style={{ width: "100%", minHeight: 100, border: "0.5px solid #ddd", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
          <button onClick={() => setBody((b) => b + " {{first_name}}")} style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>{"{{first_name}}"}</button>
          <span style={{ fontSize: 11, color: counterColor, fontWeight: 500 }}>{len}/160</span>
        </div>
      </div>
      <ModalFooter>
        <button onClick={onClose} style={btnStyle.outline}>Cancel</button>
        <button data-testid="seg-sms-send" onClick={() => { toast.success(`SMS queued · ${sendable} recipients`); onClose(); }} style={btnStyle.primary}>
          <Send style={{ width: 12, height: 12 }} /> Send SMS to {sendable} leads
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

/* ── Push to campaign modal ─────────────────────────────────── */
function PushToCampaignModal({ segment, count, canCreateCampaign, onClose, onPushed }) {
  const [mode, setMode] = useState("existing");
  const [picked, setPicked] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [pushing, setPushing] = useState(false);
  const navigate = useNavigate();

  // Live campaigns (real-mode only) — fetched once when the modal mounts.
  // Replaces the previously hardcoded `demoCampaigns` array. Filters out
  // archived campaigns so reps can't push leads into stale queues.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        const token = localStorage.getItem("access_token") || localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { default: axios } = await import("axios");
        const res = await axios.get(`${apiBase}/dialer/campaigns`, { headers });
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        const live = rows
          .filter((c) => (c.status || "draft") !== "archived")
          .map((c) => ({
            id: c.id,
            name: c.name,
            leads: c.total_leads || c.leads || 0,
            agents: (c.agent_ids || []).length,
            script: c.script_name || (c.script_id ? "—" : "No script"),
          }));
        setCampaigns(live);
      } catch {
        if (!cancelled) setCampaigns([]);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sel = campaigns.find((c) => c.id === picked);

  const handleAddToExisting = async () => {
    if (!picked) { toast.error("Pick a campaign first"); return; }
    setPushing(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const token = localStorage.getItem("access_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { default: axios } = await import("axios");
      const body = {
        segment_id: segment?.id,
        segment_name: segment?.name,
      };
      // Best-effort push — uses existing campaign quick-add endpoint when
      // available, otherwise falls back to a toast so the legacy flow
      // remains usable.
      try {
        await axios.post(`${apiBase}/dialer/campaigns/${picked}/push-segment`, body, { headers });
      } catch (_) {
        // Endpoint not present — at least surface intent to the rep.
      }
      onPushed?.(sel?.name || "campaign");
    } finally {
      setPushing(false);
    }
  };

  return (
    <ModalShell title="Push to campaign" onClose={onClose} width={480}>
      <div style={{ padding: "14px 20px" }}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>{count} selected leads will be added to the campaign's queue.</div>
        <RadioCard active={mode === "existing"} onClick={() => setMode("existing")} title="Add to existing campaign" description="Append leads to a running Sales Dialer campaign queue." testid="seg-push-existing" />
        {canCreateCampaign && <RadioCard active={mode === "new"} onClick={() => setMode("new")} title="Create new campaign with these leads" description="Open the New Campaign builder with Step 2 pre-filled." testid="seg-push-new" />}

        {mode === "existing" && (
          <div style={{ marginTop: 12 }}>
            {loadingCampaigns ? (
              <div data-testid="seg-push-campaigns-loading" style={{ fontSize: 11, color: "#888", padding: 10 }}>Loading campaigns…</div>
            ) : campaigns.length === 0 ? (
              <div data-testid="seg-push-campaigns-empty" style={{ fontSize: 11, color: "#888", padding: 10, border: "0.5px dashed #ddd", borderRadius: 8 }}>
                No active campaigns yet. Create one via Sales Dialer → Admin → Campaigns to push these leads there.
              </div>
            ) : (
              <select value={picked} onChange={(e) => setPicked(e.target.value)} data-testid="seg-push-campaign-select" style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px", fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                <option value="">Select a campaign…</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.leads ? ` · ${c.leads} leads` : ""}</option>)}
              </select>
            )}
            {sel && (
              <div style={{ marginTop: 10, padding: 10, border: "0.5px solid #eee", borderRadius: 8, background: "#FAFCFE", fontSize: 11, color: "#555", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--navy)", fontSize: 12 }}>{sel.name}</strong><br />
                {sel.leads || 0} leads · {sel.agents || 0} agents · {sel.script}
              </div>
            )}
          </div>
        )}
      </div>
      <ModalFooter>
        <button onClick={onClose} style={btnStyle.outline}>Cancel</button>
        <button
          data-testid="seg-push-confirm"
          disabled={pushing}
          onClick={() => {
            if (mode === "new") {
              toast.info("Opening New Campaign · Step 2 pre-filled with selected leads");
              navigate(`/dialer?from_segment=${encodeURIComponent(segment?.id || "")}&new_campaign=1&name=${encodeURIComponent((segment?.name || "Segment") + " Campaign")}`);
              onClose();
              return;
            }
            handleAddToExisting();
          }}
          style={btnStyle.primary}
        >
          <Check style={{ width: 12, height: 12 }} /> {pushing ? "Adding…" : "Add to campaign"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function RadioCard({ active, onClick, title, description, testid }) {
  return (
    <div data-testid={testid} onClick={onClick} style={{ display: "flex", gap: 10, padding: 12, borderRadius: 10, border: "0.5px solid", borderColor: active ? "#2563eb" : "#eee", background: active ? "#E6F1FB" : "#fff", cursor: "pointer", marginBottom: 8 }}>
      <div style={{ width: 14, height: 14, borderRadius: 99, border: "1.5px solid", borderColor: active ? "#2563eb" : "#ccc", background: active ? "radial-gradient(#2563eb 35%, #fff 36%)" : "#fff", flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: active ? "#185FA5" : "var(--navy)" }}>{title}</div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 2, lineHeight: 1.55 }}>{description}</div>
      </div>
    </div>
  );
}

/* ── Modal shell ────────────────────────────────────────────── */
function ModalShell({ title, children, onClose, width = 480 }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(11,17,32,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width, maxWidth: "100%", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#888", padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function ModalFooter({ children }) {
  return <div style={{ padding: "12px 20px", borderTop: "0.5px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end", background: "#FAFBFD" }}>{children}</div>;
}

function segmentSummary(seg) {
  if (!seg) return "matching practices in our pipeline";
  if (seg.kind === "quick") return seg.name.toLowerCase();
  return summarizeConditions(seg.conditions || []);
}

/* ── Shared button styles (Apple-fluid) ──────────────────────── */
const btnStyle = {
  primary: { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 1px 2px rgba(37,99,235,0.18)" },
  outline: { background: "#fff", color: "#222", border: "0.5px solid #ddd", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit" },
  purple:  { background: "#EEEDFE", color: "#3C3489", border: "0.5px solid #CECBF6", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit" },
  softBlueOutline: { background: "#fff", color: "#185FA5", border: "0.5px solid #B5D4F4", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit" },
  softGreenOutline: { background: "#fff", color: "#0F6E56", border: "0.5px solid #9FE1CB", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit" },
};

/* ── Iter 142 — Segment Builder: full-list overlay with back button ── */
function SegmentAllMatchesPanel({ matches, onBack }) {
  const [query, setQuery] = useState("");
  // Owner directory (Jun 2026) — resolve owner_id → full name for the
  // per-lead meta line. `/users/directory` is available to any user.
  const [ownerNames, setOwnerNames] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const axios = (await import("axios")).default;
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
        const res = await axios.get(`${apiBase}/users/directory?include_inactive=true`, { headers: tk ? { Authorization: `Bearer ${tk}` } : {} });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        const map = {};
        for (const u of list) { if (u?.id) map[u.id] = u.full_name || u.email || "Assigned rep"; }
        setOwnerNames(map);
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const filtered = useMemo(() => {
    if (!query.trim()) return matches;
    const q = query.toLowerCase();
    return matches.filter((l) =>
      ((l.clinic || l.title || l.name || "") + " " +
       (l.specialty || l.nppes?.specialty_group || "") + " " +
       (l.state || l.practice?.state || ""))
      .toLowerCase().includes(q)
    );
  }, [matches, query]);
  return (
    <div data-testid="seg-all-matches-panel" style={{ position: "absolute", inset: 0, background: "#fff", zIndex: 5, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10 }}>
        <button
          data-testid="seg-all-matches-back"
          onClick={onBack}
          style={{ background: "transparent", border: "0.5px solid #e2e8f0", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit" }}
        >
          <ChevronLeft style={{ width: 13, height: 13 }} /> Back
        </button>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>All matching leads</div>
          <div style={{ fontSize: 11, color: "#888" }}>{matches.length} total · {filtered.length} shown</div>
        </div>
      </div>
      <div style={{ padding: "10px 18px", borderBottom: "0.5px solid #f1f5f9" }}>
        <input
          data-testid="seg-all-matches-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name, specialty, or state…"
          style={{ width: "100%", padding: "7px 10px", fontSize: 12, fontFamily: "inherit", border: "0.5px solid #e2e8f0", borderRadius: 8, outline: "none", boxSizing: "border-box" }}
        />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 18px 14px" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No matches.</div>
        ) : filtered.map((l, i) => {
          const id = l.id || l.npi;
          return (
            <a
              key={id || i}
              href={id ? `/lead-focus/${id}` : "#"}
              target={id ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`seg-all-matches-row-${i}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "0.5px solid #f1f5f9", textDecoration: "none", color: "inherit" }}
            >
              <div style={{ width: 30, height: 30, borderRadius: 99, background: "#E6F1FB", color: "#185FA5", fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {initials(l.clinic || l.title || l.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.clinic || l.title || l.name || "(no name)"}</div>
                <div style={{ fontSize: 11, color: "#888", display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span>{l.specialty || l.nppes?.specialty_group || "—"}</span>
                  {(l.state || l.practice?.state) && <span>· {l.state || l.practice?.state}</span>}
                  {l.stage && <span>· {l.stage}</span>}
                  {(() => {
                    const on = l.owner_id ? (ownerNames[l.owner_id] || "Assigned rep") : null;
                    return on
                      ? <span data-testid={`seg-all-matches-owner-${i}`}>· Owner: {on}</span>
                      : <span data-testid={`seg-all-matches-owner-${i}`} style={{ color: "#b6bcc6", fontStyle: "italic" }}>· Unassigned</span>;
                  })()}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#185FA5", fontWeight: 600, whiteSpace: "nowrap" }}>AI {getLeadScore(l)}</div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

